import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import VerificationBanner from '../components/VerificationBanner';
import CancelCampaignModal from '../components/CancelCampaignModal';
import {
  getBrandDashboard,
  getBrandCampaigns,
  getCampaignAnalytics,
  getSavedCreators,
  getBrandFunds,
  getBrandRefunds,
  getCampaignDetails,
  resendCampaignInvitations,
  resendInvitationEmail,
  reviewSubmission,
  getCampaignPerformance,
  refreshCampaignPerformance,
  getBrandCampaignCreators,
  saveCreatorToCRM,
  getAllCreators,
  isAuthenticated,
  getUserType,
  getBrandBankDetails,
  saveBrandBankDetails,
  deleteBrandBankDetails,
  recoverPayment,
  finalizeCampaign
} from '../api/api';
import { IS_PRE_LAUNCH } from '../config/launchMode';
import './BrandDashboard.css';

/**
 * One-time launch announcement strip for the Performance Hub product.
 *
 * Stays out of the main brand-dashboard data flow on purpose:
 * - dismissal state lives in localStorage (`ph_launch_banner_dismissed_v1`)
 *   so existing brands see it once and dismissal survives reloads,
 * - bumping the suffix (`_v1` → `_v2`) re-introduces it for a future
 *   relaunch / pricing change.
 */
function PerformanceHubLaunchBanner() {
  const STORAGE_KEY = 'ph_launch_banner_dismissed_v1';
  const [hidden, setHidden] = React.useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (hidden) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* private mode — banner just won't be permanently dismissed */
    }
    setHidden(true);
  };
  return (
    <div className="ph-launch-banner" role="status" aria-live="polite">
      <span className="ph-launch-banner-pill">LIVE</span>
      <span className="ph-launch-banner-text">
        <strong>Performance Hub is live.</strong> Track Instagram performance for creator lists you already
        have — CSV import, magic-link connect, and metric sync, separate from Milan campaigns.
      </span>
      <Link to="/performance" className="ph-launch-banner-cta">
        Open Performance Hub →
      </Link>
      <button
        type="button"
        className="ph-launch-banner-close"
        aria-label="Dismiss Performance Hub launch banner"
        onClick={dismiss}
      >
        ×
      </button>
    </div>
  );
}

function formatApiError(err, fallback = 'Request failed') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === 'object' && x?.msg ? x.msg : JSON.stringify(x)))
      .join('; ');
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || fallback;
}

function BrandDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize activeTab from URL or default to 'overview'
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['overview', 'blueprint', 'live', 'completed', 'crm', 'funds', 'cancelled', 'refunds', 'settings'];
    return validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview';
  });
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [campaigns, setCampaigns] = useState({ draft: [], live: [], completed: [], cancelled: [] });
  // Drafts where Razorpay confirms a captured payment but our system never
  // saw the verify/finalize call (e.g. brand refreshed mid-checkout). The
  // dashboard surfaces these as a banner with a one-click "Send invitations
  // now" CTA. Shape: { [campaign_id]: { status, amount, razorpay_order_id,
  // razorpay_payment_id, finalizing, error } }.
  const [recoverableDrafts, setRecoverableDrafts] = useState({});
  const [savedCreators, setSavedCreators] = useState([]);
  const [fundsData, setFundsData] = useState(null);
  const [refundsData, setRefundsData] = useState(null);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundFilter, setRefundFilter] = useState('completed'); // 'in_progress', 'failed', 'completed'
  const [fundsSearch, setFundsSearch] = useState('');
  const [fundsStatusFilter, setFundsStatusFilter] = useState('all');
  
  // Bank Details State
  const [bankDetails, setBankDetails] = useState(null);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankDetailsForm, setBankDetailsForm] = useState({
    payment_method: 'bank',
    upi_id: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    account_type: 'savings'
  });
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankDetailsMessage, setBankDetailsMessage] = useState({ type: '', text: '' });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [resendingEmails, setResendingEmails] = useState(false);
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [campaignPerformance, setCampaignPerformance] = useState(null);
  const [campaignDetailsError, setCampaignDetailsError] = useState(null);
  const campaignLoadSeqRef = useRef(0);

  // Cancel campaign modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [campaignToCancel, setCampaignToCancel] = useState(null);
  const [refreshingPerformance, setRefreshingPerformance] = useState(false);
  const [campaignCreators, setCampaignCreators] = useState([]);
  const [savingCreator, setSavingCreator] = useState(null);
  const [crmView, setCrmView] = useState('saved'); // 'saved', 'campaign', or 'search'
  const [creatorStatusFilter, setCreatorStatusFilter] = useState('pending_response'); // 'pending_response', 'waiting', 'pending_review', 'approved', 'rejected'
  
  // CRM Search state
  const [crmSearchQuery, setCrmSearchQuery] = useState('');
  const [crmSearchResults, setCrmSearchResults] = useState([]);
  const [crmSearchLoading, setCrmSearchLoading] = useState(false);
  const [crmSearchFilters, setCrmSearchFilters] = useState({
    niche: '',
    minFollowers: '',
    maxFollowers: ''
  });
  const [usingOfflineData, setUsingOfflineData] = useState(false);

  // Brand ID from auth
  const brandId = localStorage.getItem('userId') || 'mock-brand-id';
  
  // Check authentication - redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      // Save the current URL to redirect back after login
      const returnUrl = location.pathname + location.search;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}&type=brand`);
    } else if (getUserType() !== 'brand') {
      // If logged in but not as a brand, redirect to appropriate dashboard
      navigate('/creator-dashboard');
    }
  }, [navigate, location]);
  
  // Keep URL `tab` in sync with activeTab. Only preserve `campaign` when it
  // matches the drilled-in campaign — otherwise switching Live ↔ Completed
  // would re-attach a stale ?campaign= from the old tab and auto-select
  // would reopen the report under the wrong section.
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const urlCampaign = searchParams.get('campaign');
    const selId = selectedCampaign
      ? String(selectedCampaign.id || selectedCampaign._id)
      : null;

    if (currentTab !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      if (!selId || urlCampaign !== selId) {
        next.delete('campaign');
      }
      setSearchParams(next, { replace: true });
      return;
    }

    if (!selectedCampaign && urlCampaign) {
      const next = new URLSearchParams(searchParams);
      next.delete('campaign');
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams, selectedCampaign]);

  // Auto-select campaign from URL parameter once campaigns are loaded.
  // If the URL points to a campaign that no longer exists (e.g. it was
  // deleted, or the user has a stale bookmark), drop the `campaign` param
  // so the dashboard renders the live-list view instead of spinning on
  // "Loading campaign..." forever.
  useEffect(() => {
    if (loading) return;  // wait until dashboard data has loaded
    const campaignIdFromUrl = searchParams.get('campaign');
    if (!campaignIdFromUrl || selectedCampaign) return;

    const matchById = (c) => c.id === campaignIdFromUrl || c._id === campaignIdFromUrl;
    const campaign =
      campaigns.live.find(matchById) ||
      campaigns.completed.find(matchById) ||
      campaigns.cancelled.find(matchById) ||
      campaigns.draft.find(matchById);

    if (campaign) {
      console.log('Auto-selecting campaign from URL:', campaign.campaign_name || campaign.name);
      setSelectedCampaign(campaign);
      fetchCampaignAnalytics(campaign.id || campaign._id);
      return;
    }

    console.warn('Campaign id from URL not found in any list, dropping ?campaign param:', campaignIdFromUrl);
    const next = new URLSearchParams(searchParams);
    next.delete('campaign');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, campaigns, loading]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setUsingOfflineData(false);
      
      let usingFallback = false;
      const [dashRes, campaignsRes, creatorsRes, fundsRes] = await Promise.all([
        getBrandDashboard(brandId).catch(() => { usingFallback = true; return { data: getMockDashboard() }; }),
        getBrandCampaigns(brandId).catch(() => { usingFallback = true; return { data: { campaigns: getMockCampaigns() } }; }),
        getSavedCreators(brandId).catch(() => { usingFallback = true; return { data: { creators: getMockSavedCreators() } }; }),
        getBrandFunds(brandId).catch(() => { usingFallback = true; return { data: getMockFunds() }; })
      ]);

      if (usingFallback) {
        setUsingOfflineData(true);
      }

      setDashboardData(dashRes.data);
      
      const allCampaigns = campaignsRes.data.campaigns || [];
      const liveStatuses = ['live', 'invitations_sent', 'in_progress', 'content_review', 'monitoring'];
      
      console.log('All campaigns from API:', allCampaigns);
      console.log('Campaign statuses:', allCampaigns.map(c => ({ name: c.campaign_name || c.name, status: c.status })));
      
      const liveCampaigns = allCampaigns.filter(c => liveStatuses.includes(c.status));
      console.log('Filtered live campaigns:', liveCampaigns);
      
      const drafts = allCampaigns.filter(c => c.status === 'draft');
      setCampaigns({
        draft: drafts,
        live: liveCampaigns,
        completed: allCampaigns.filter(c => c.status === 'completed'),
        cancelled: allCampaigns.filter(c => c.status === 'cancelled')
      });
      
      setSavedCreators(creatorsRes.data.creators || []);
      setFundsData(fundsRes.data);

      // After we have the drafts list, ask the backend to reconcile each one
      // with Razorpay. This catches the "paid but never finalized" failure
      // mode (refresh during /verify-payment, network drop, etc) and lets
      // the brand resume from a banner without re-opening checkout.
      scanDraftsForOrphanedPayments(drafts);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setUsingOfflineData(true);
      setDashboardData(getMockDashboard());
      const mockCampaigns = getMockCampaigns();
      const liveStatuses = ['live', 'invitations_sent', 'in_progress', 'content_review', 'monitoring'];
      setCampaigns({
        draft: mockCampaigns.filter(c => c.status === 'draft'),
        live: mockCampaigns.filter(c => liveStatuses.includes(c.status)),
        completed: mockCampaigns.filter(c => c.status === 'completed'),
        cancelled: mockCampaigns.filter(c => c.status === 'cancelled')
      });
      setSavedCreators(getMockSavedCreators());
      setFundsData(getMockFunds());
    } finally {
      setLoading(false);
    }
  };

  // For every draft, ask the backend whether Razorpay actually captured the
  // payment. If it did but our system never finalized (the bug where the
  // brand refreshed before /verify-payment finished), we surface a one-click
  // "Send invitations now" banner. We swallow per-campaign errors so a
  // single failure doesn't hide other recoverable campaigns.
  const scanDraftsForOrphanedPayments = async (drafts) => {
    if (!drafts || drafts.length === 0) {
      setRecoverableDrafts({});
      return;
    }
    const results = await Promise.all(
      drafts.map(async (c) => {
        const cid = c.id || c._id;
        if (!cid) return null;
        try {
          const res = await recoverPayment(cid);
          if (res?.data?.ready_to_finalize) {
            return [cid, {
              campaign_name: c.name || c.campaign_name,
              amount: res.data.amount,
              razorpay_order_id: res.data.razorpay_order_id,
              razorpay_payment_id: res.data.razorpay_payment_id,
              recovered: !!res.data.recovered,
            }];
          }
        } catch (_) {
          // ignore — drafts without payments will return ready_to_finalize=false
        }
        return null;
      })
    );
    const map = {};
    results.forEach(entry => {
      if (entry) map[entry[0]] = entry[1];
    });
    setRecoverableDrafts(map);
  };

  // Brand clicks "Send invitations now" on the recovery banner. Server
  // already has the locked payment + the saved allocation, so we just send
  // the campaign_id and let the recovery-friendly /finalize endpoint fill
  // in the rest.
  const handleResumeFinalize = async (campaignId) => {
    if (!campaignId) return;
    setRecoverableDrafts(prev => ({
      ...prev,
      [campaignId]: { ...(prev[campaignId] || {}), finalizing: true, error: null },
    }));
    try {
      const res = await finalizeCampaign({ campaign_id: campaignId });
      if (res?.data?.success) {
        setRecoverableDrafts(prev => {
          const copy = { ...prev };
          delete copy[campaignId];
          return copy;
        });
        await fetchDashboardData();
        // Land them on the live tab so they immediately see the recovered
        // campaign, with the campaign param so the detail view auto-opens.
        navigate(`/brand-dashboard?tab=live&campaign=${campaignId}`, { replace: true });
      } else {
        throw new Error(res?.data?.message || 'Finalize failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to finalize campaign';
      setRecoverableDrafts(prev => ({
        ...prev,
        [campaignId]: { ...(prev[campaignId] || {}), finalizing: false, error: msg },
      }));
    }
  };

  // Centralized "open campaign detail view" — also writes the campaign id
  // into the URL so a browser refresh keeps the user on the same view
  // instead of bouncing back to the live-campaigns list.
  const selectCampaign = (campaign) => {
    if (!campaign) return;
    const cid = campaign.id || campaign._id;
    setSelectedCampaign(campaign);
    fetchCampaignAnalytics(cid);
    const detailTab = campaign.status === 'completed' ? 'completed' : 'live';
    setActiveTab(detailTab);
    setSearchParams({ tab: detailTab, campaign: cid }, { replace: true });
  };

  /** Sidebar navigation to Live / Completed — clears drill-in so a completed report does not reappear under Live. */
  const exitCampaignDetailToTab = (tab) => {
    setSelectedCampaign(null);
    setCampaignDetails(null);
    setCampaignDetailsError(null);
    setCampaignPerformance(null);
    setCampaignAnalytics(null);
    setCreatorStatusFilter('pending_response');
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('campaign');
      return next;
    }, { replace: true });
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    const seq = ++campaignLoadSeqRef.current;
    setCampaignAnalytics(null);
    setCampaignDetails(null);
    setCampaignPerformance(null);
    setCampaignDetailsError(null);

    const results = await Promise.allSettled([
      getCampaignAnalytics(campaignId),
      getCampaignDetails(campaignId),
      getCampaignPerformance(campaignId),
    ]);

    if (seq !== campaignLoadSeqRef.current) return;

    const [analyticsOutcome, detailsOutcome, perfOutcome] = results;

    if (analyticsOutcome.status === 'fulfilled' && analyticsOutcome.value?.data) {
      setCampaignAnalytics(analyticsOutcome.value.data);
    } else {
      console.error(
        'Campaign analytics failed:',
        analyticsOutcome.status === 'rejected' ? analyticsOutcome.reason : analyticsOutcome.value
      );
      setCampaignAnalytics(getMockAnalytics());
    }

    if (detailsOutcome.status === 'fulfilled' && detailsOutcome.value?.data) {
      setCampaignDetails(detailsOutcome.value.data);
      setCampaignDetailsError(null);
    } else {
      setCampaignDetails(null);
      const err =
        detailsOutcome.status === 'rejected'
          ? detailsOutcome.reason
          : new Error('Campaign details response empty');
      setCampaignDetailsError(formatApiError(err, 'Failed to load campaign details'));
    }

    if (perfOutcome.status === 'fulfilled' && perfOutcome.value?.data) {
      setCampaignPerformance(perfOutcome.value.data);
    } else {
      setCampaignPerformance(null);
    }
  };

  const handleSendReminders = async (campaignId) => {
    if (resendingEmails) return;

    try {
      setResendingEmails(true);
      const res = await resendCampaignInvitations(campaignId);
      if (res.data.success) {
        let message = res.data.message;
        if (res.data.info) {
          message += `\n\n${res.data.info}`;
        }
        if (res.data.max_reached > 0) {
          message += `\n(${res.data.max_reached} creator(s) already reached max reminders)`;
        }
        alert(message);
        // Refresh campaign details
        const detailsRes = await getCampaignDetails(campaignId);
        if (detailsRes?.data) {
          setCampaignDetails(detailsRes.data);
          setCampaignDetailsError(null);
        }
      }
    } catch (err) {
      alert('Failed to send reminders: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResendingEmails(false);
    }
  };

  // Per-creator email resend, used when the brand says "creator X claims
  // they didn't get the email — send it again". Tracked in its own
  // counter on the backend so it doesn't burn through the bulk-reminder
  // cap. We block the button while a request is in flight per invitation.
  const [resendingInviteIds, setResendingInviteIds] = useState({});
  const handleResendOneInvitation = async (campaignId, invitationId) => {
    if (!invitationId || resendingInviteIds[invitationId]) return;
    setResendingInviteIds(prev => ({ ...prev, [invitationId]: true }));
    try {
      const res = await resendInvitationEmail(campaignId, invitationId);
      if (res?.data?.success) {
        const remaining = (res.data.max_manual_resends || 3) - (res.data.manual_resend_count || 0);
        alert(
          `📧 Resending invitation email.\n\n` +
          `If the creator still doesn't see it, ask them to check their spam folder ` +
          `or update their registered email address.\n\n` +
          `(${remaining} manual resend(s) left for this creator)`
        );
        const detailsRes = await getCampaignDetails(campaignId);
        if (detailsRes?.data) {
          setCampaignDetails(detailsRes.data);
          setCampaignDetailsError(null);
        }
      }
    } catch (err) {
      alert('Failed to resend invitation: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setResendingInviteIds(prev => {
        const copy = { ...prev };
        delete copy[invitationId];
        return copy;
      });
    }
  };

  const handleReviewSubmission = async (submissionId, action, feedback = '') => {
    if (!submissionId) {
      alert('Error: No submission ID found. The creator may not have submitted content yet.');
      return;
    }
    
    try {
      setReviewingSubmission(submissionId);
      const res = await reviewSubmission({
        submission_id: submissionId,
        action: action,
        feedback: feedback
      });
      
      if (res.data.success) {
        alert(res.data.message);
        // Refresh campaign details to update creator status
        if (selectedCampaign) {
          const cid = selectedCampaign.id || selectedCampaign._id;
          const detailsRes = await getCampaignDetails(cid);
          if (detailsRes?.data) {
            setCampaignDetails(detailsRes.data);
            setCampaignDetailsError(null);
          }
        }
      }
    } catch (err) {
      console.error('Review submission error:', err);
      alert('Failed to review submission: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReviewingSubmission(null);
    }
  };

  const handleRefreshCampaignPerformance = async () => {
    if (!selectedCampaign) return;
    const cid = selectedCampaign.id || selectedCampaign._id;

    setRefreshingPerformance(true);
    try {
      const res = await refreshCampaignPerformance(cid);
      if (res.data.success) {
        alert(res.data.message);
        // Refresh performance data
        const perfRes = await getCampaignPerformance(cid);
        if (perfRes?.data) {
          setCampaignPerformance(perfRes.data);
        }
      }
    } catch (err) {
      alert('Failed to refresh metrics: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshingPerformance(false);
    }
  };

  const fetchCampaignCreators = async () => {
    try {
      const res = await getBrandCampaignCreators(brandId);
      if (res.data?.creators) {
        setCampaignCreators(res.data.creators);
      }
    } catch (err) {
      console.error('Error fetching campaign creators:', err);
      setCampaignCreators([]);
    }
  };

  const handleSaveCreatorToCRM = async (creator, tags = []) => {
    try {
      setSavingCreator(creator.id);
      
      // Auto-generate tags based on performance
      const autoTags = [];
      if (creator.avg_engagement_rate > 5) autoTags.push('High Engagement');
      if (creator.total_campaigns > 2) autoTags.push('Repeat Collaborator');
      if (creator.total_earned > 50000) autoTags.push('High Value');
      
      const res = await saveCreatorToCRM(brandId, {
        creator_id: creator.id,
        tags: [...autoTags, ...tags],
        notes: `Added from campaign. Total campaigns: ${creator.total_campaigns}`
      });
      
      if (res.data.success) {
        // Update local state to show as saved
        setCampaignCreators(prev => 
          prev.map(c => c.id === creator.id ? { ...c, is_saved: true } : c)
        );
        // Refresh saved creators list
        try {
          const creatorsRes = await getSavedCreators(brandId);
          if (creatorsRes.data?.creators) {
            setSavedCreators(creatorsRes.data.creators);
          }
        } catch (e) {
          console.error('Error refreshing saved creators:', e);
        }
      }
    } catch (err) {
      alert('Failed to save creator: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingCreator(null);
    }
  };

  // Search creators for CRM
  const handleCrmSearch = async () => {
    if (!crmSearchQuery.trim() && !crmSearchFilters.niche && !crmSearchFilters.minFollowers && !crmSearchFilters.maxFollowers) {
      return;
    }
    
    setCrmSearchLoading(true);
    try {
      const response = await getAllCreators({
        search: crmSearchQuery || undefined,
        niche: crmSearchFilters.niche || undefined,
        minFollowers: crmSearchFilters.minFollowers || undefined,
        maxFollowers: crmSearchFilters.maxFollowers || undefined,
        limit: 20
      });
      
      const results = response.data.creators || [];
      // Mark creators that are already saved
      const savedIds = new Set(savedCreators.map(c => c.id || c._id));
      const resultsWithSavedStatus = results.map(c => ({
        ...c,
        id: c.id || c._id,
        is_saved: savedIds.has(c.id || c._id)
      }));
      
      setCrmSearchResults(resultsWithSavedStatus);
    } catch (err) {
      console.error('Error searching creators:', err);
      setCrmSearchResults([]);
    } finally {
      setCrmSearchLoading(false);
    }
  };

  // Save creator from search results to CRM
  const handleSaveSearchCreatorToCRM = async (creator) => {
    try {
      setSavingCreator(creator.id);
      
      // Auto-generate tags based on profile
      const autoTags = [];
      if ((creator.instagram_engagement_rate || 0) > 5) autoTags.push('High Engagement');
      if ((creator.instagram_followers_count || 0) > 100000) autoTags.push('Large Following');
      if (creator.instagram_detected_niches?.length > 0) {
        autoTags.push(creator.instagram_detected_niches[0]);
      }
      
      const res = await saveCreatorToCRM(brandId, {
        creator_id: creator.id,
        tags: autoTags,
        notes: `Added from search`
      });
      
      if (res.data.success) {
        // Update search results to show as saved
        setCrmSearchResults(prev => 
          prev.map(c => c.id === creator.id ? { ...c, is_saved: true } : c)
        );
        // Refresh saved creators list
        try {
          const creatorsRes = await getSavedCreators(brandId);
          if (creatorsRes.data?.creators) {
            setSavedCreators(creatorsRes.data.creators);
          }
        } catch (e) {
          console.error('Error refreshing saved creators:', e);
        }
      }
    } catch (err) {
      alert('Failed to save creator: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingCreator(null);
    }
  };

  // Fetch campaign creators when CRM tab is active and view changes to 'campaign'
  useEffect(() => {
    if (activeTab === 'crm' && crmView === 'campaign') {
      fetchCampaignCreators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, crmView]);

  // Fetch refunds whenever a tab that needs refund data is active.
  // ``cancelled`` joins the list because the Cancelled Campaigns view
  // hydrates each campaign's Refund Status panel from refundsData; landing
  // there directly (e.g. via a deep link) used to render empty until the
  // user navigated elsewhere and back.
  useEffect(() => {
    if (
      activeTab === 'refunds' ||
      activeTab === 'funds' ||
      activeTab === 'overview' ||
      activeTab === 'cancelled'
    ) {
      fetchRefunds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchRefunds = async () => {
    setRefundsLoading(true);
    try {
      const res = await getBrandRefunds();
      if (res.data?.success) {
        setRefundsData(res.data);
      }
    } catch (err) {
      console.error('Error fetching refunds:', err);
    } finally {
      setRefundsLoading(false);
    }
  };

  // Fetch bank details when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      fetchBankDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchBankDetails = async () => {
    const brandId = localStorage.getItem('userId');
    if (!brandId) return;
    
    setBankDetailsLoading(true);
    try {
      const res = await getBrandBankDetails(brandId);
      if (res.data?.success && res.data?.bank_details) {
        setBankDetails(res.data.bank_details);
        setBankDetailsForm({
          payment_method: res.data.bank_details.payment_method || 'bank',
          upi_id: res.data.bank_details.upi_id || '',
          account_holder_name: res.data.bank_details.account_holder_name || '',
          account_number: res.data.bank_details.account_number || '',
          ifsc_code: res.data.bank_details.ifsc_code || '',
          bank_name: res.data.bank_details.bank_name || '',
          account_type: res.data.bank_details.account_type || 'savings'
        });
      }
    } catch (err) {
      console.error('Error fetching bank details:', err);
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const handleSaveBankDetails = async (e) => {
    e.preventDefault();
    const brandId = localStorage.getItem('userId');
    if (!brandId) return;

    setSavingBankDetails(true);
    setBankDetailsMessage({ type: '', text: '' });

    try {
      const res = await saveBrandBankDetails(brandId, bankDetailsForm);
      if (res.data?.success) {
        setBankDetails(res.data.bank_details);
        setBankDetailsMessage({ type: 'success', text: 'Bank details saved successfully!' });
        setTimeout(() => setBankDetailsMessage({ type: '', text: '' }), 3000);
      }
    } catch (err) {
      setBankDetailsMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to save bank details' 
      });
    } finally {
      setSavingBankDetails(false);
    }
  };

  const handleDeleteBankDetails = async () => {
    const brandId = localStorage.getItem('userId');
    if (!brandId) return;
    
    if (!window.confirm('Are you sure you want to delete your bank details?')) return;

    try {
      const res = await deleteBrandBankDetails(brandId);
      if (res.data?.success) {
        setBankDetails(null);
        setBankDetailsForm({
          payment_method: 'bank',
          upi_id: '',
          account_holder_name: '',
          account_number: '',
          ifsc_code: '',
          bank_name: '',
          account_type: 'savings'
        });
        setBankDetailsMessage({ type: 'success', text: 'Bank details deleted successfully!' });
        setTimeout(() => setBankDetailsMessage({ type: '', text: '' }), 3000);
      }
    } catch (err) {
      setBankDetailsMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to delete bank details' 
      });
    }
  };

  const getMockDashboard = () => ({
    overview: {
      total_campaigns: 8,
      live_campaigns: 3,
      completed_campaigns: 4,
      draft_campaigns: 1,
      saved_creators: 25,
      total_budget: 500000,
      total_spent: 320000
    }
  });

  const getMockCampaigns = () => [
    { id: '1', name: 'Summer Fashion Launch', status: 'live', budget: 150000, start_date: '2024-04-01', selected_creators: ['c1', 'c2', 'c3'] },
    { id: '2', name: 'Tech Product Review', status: 'live', budget: 100000, start_date: '2024-04-05', selected_creators: ['c4', 'c5'] },
    { id: '3', name: 'Food Delivery Promo', status: 'completed', budget: 80000, start_date: '2024-03-01', selected_creators: ['c6'] },
    { id: '4', name: 'Fitness Challenge', status: 'draft', budget: 200000, start_date: '2024-05-01', selected_creators: [] }
  ];

  const getMockSavedCreators = () => [
    { id: 'c1', name: 'Priya Sharma', instagram_followers_count: 250000, instagram_detected_niches: ['Fashion'], crm_data: { tags: ['Top Performer'] } },
    { id: 'c2', name: 'Rahul Verma', instagram_followers_count: 180000, instagram_detected_niches: ['Tech'], crm_data: { tags: ['Reliable'] } }
  ];

  const getMockFunds = () => ({
    overview: {
      total_budget: 500000,
      total_blocked: 250000,
      total_spent: 180000,
      total_refunds: 15000,
      available_balance: 305000
    },
    campaigns: [
      { id: '1', name: 'Summer Fashion', budget: 150000, funds_blocked: 100000, status: 'live' },
      { id: '2', name: 'Tech Review', budget: 100000, funds_blocked: 80000, status: 'live' }
    ]
  });

  const getMockAnalytics = () => ({
    total_reach: 125000,
    total_impressions: 450000,
    total_engagement: 15600,
    engagement_rate: 3.47,
    total_likes: 12500,
    total_comments: 2100,
    total_shares: 980,
    budget_spent: 85000,
    budget_remaining: 65000,
    cost_per_click: 2.45,
    sentiment: { positive: 72, neutral: 21, negative: 7 }
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'draft': { label: 'Draft', class: 'draft' },
      'live': { label: 'Live', class: 'live' },
      'invitations_sent': { label: 'Invitations Sent', class: 'live' },
      'in_progress': { label: 'In Progress', class: 'live' },
      'content_review': { label: 'Content Review', class: 'live' },
      'monitoring': { label: 'Monitoring', class: 'live' },
      'completed': { label: 'Completed', class: 'completed' }
    };
    return statusMap[status] || { label: status, class: 'draft' };
  };

  if (loading) {
    return (
      <div className="brand-dashboard">
        <Navbar />
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="brand-dashboard">
      <Navbar />
      
      {usingOfflineData && (
        <div className="offline-banner" style={{
          background: '#fef3c7',
          color: '#92400e',
          padding: '12px 20px',
          textAlign: 'center',
          fontSize: '14px',
          borderBottom: '1px solid #fbbf24'
        }}>
          ⚠️ Unable to connect to server. Showing sample data. Please check your connection and refresh.
        </div>
      )}

      <PerformanceHubLaunchBanner />

      
      <div className="dashboard-container">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-header">
            <h2>Brand Dashboard</h2>
          </div>
          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <span className="nav-icon">📊</span>
              Overview
            </button>
            {IS_PRE_LAUNCH ? (
              <span
                className="nav-item create-campaign-link pre-launch-nav-disabled"
                title="Paid campaigns open at full launch. Explore the rest of the dashboard for now."
              >
                <span className="nav-icon">➕</span>
                Create Campaign
              </span>
            ) : (
              <Link
                to="/create-campaign"
                className="nav-item create-campaign-link"
              >
                <span className="nav-icon">➕</span>
                Create Campaign
              </Link>
            )}
            <button 
              className={`nav-item ${activeTab === 'blueprint' ? 'active' : ''}`}
              onClick={() => setActiveTab('blueprint')}
            >
              <span className="nav-icon">📋</span>
              Draft Campaigns
            </button>
            <button 
              className={`nav-item ${activeTab === 'live' ? 'active' : ''}`}
              onClick={() => exitCampaignDetailToTab('live')}
            >
              <span className="nav-icon">🔴</span>
              Live Campaigns
              {campaigns.live.length > 0 && <span className="badge">{campaigns.live.length}</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => exitCampaignDetailToTab('completed')}
            >
              <span className="nav-icon">✅</span>
              Completed
            </button>
            <button 
              className={`nav-item ${activeTab === 'crm' ? 'active' : ''}`}
              onClick={() => setActiveTab('crm')}
            >
              <span className="nav-icon">👥</span>
              Creator CRM
            </button>
            <button 
              className={`nav-item ${activeTab === 'funds' ? 'active' : ''}`}
              onClick={() => setActiveTab('funds')}
            >
              <span className="nav-icon">💰</span>
              Campaign Funds
            </button>
            <button 
              className={`nav-item ${activeTab === 'cancelled' ? 'active' : ''}`}
              onClick={() => setActiveTab('cancelled')}
            >
              <span className="nav-icon">🚫</span>
              Cancelled Campaigns
              {campaigns.cancelled.length > 0 && <span className="badge red">{campaigns.cancelled.length}</span>}
            </button>
            <button
              className={`nav-item ${activeTab === 'refunds' ? 'active' : ''}`}
              onClick={() => setActiveTab('refunds')}
            >
              <span className="nav-icon">↩️</span>
              Refunds
            </button>
            <button
              className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <span className="nav-icon">⚙️</span>
              Settings
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <VerificationBanner />

          {/* Recoverable payment banner - shown across all tabs.
              Triggers when Razorpay confirms a captured payment for a draft
              campaign that was never finalized (e.g. user refreshed during
              checkout). One-click finalize uses server-side state, so the
              brand never has to redo the payment. */}
          {Object.keys(recoverableDrafts).length > 0 && (
            <div className="alerts-section" style={{ marginBottom: 16 }}>
              {Object.entries(recoverableDrafts).map(([cid, info]) => (
                <div
                  key={cid}
                  className="alert-banner warning"
                  style={{ borderLeft: '4px solid #f59e0b', alignItems: 'flex-start' }}
                >
                  <span className="alert-icon">💳</span>
                  <div className="alert-content" style={{ flex: 1 }}>
                    <h4>Payment received — campaign not finalized</h4>
                    <p>
                      We received your payment of <strong>{formatCurrency(info.amount || 0)}</strong>
                      {' '}for <strong>{info.campaign_name || 'this campaign'}</strong>, but the
                      browser closed before invitations were sent. Click below to send invitations now —
                      no need to pay again.
                    </p>
                    {info.error && (
                      <p style={{ color: '#dc2626', marginTop: 6, fontSize: 13 }}>
                        {info.error}
                      </p>
                    )}
                  </div>
                  <button
                    className="btn-primary"
                    style={{ marginLeft: 12, whiteSpace: 'nowrap' }}
                    onClick={() => handleResumeFinalize(cid)}
                    disabled={!!info.finalizing}
                  >
                    {info.finalizing ? 'Sending…' : 'Send invitations now'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-content overview-page">
              <h1>Dashboard Overview</h1>

              {/* Alert Banners */}
              <div className="alerts-section">
                {/* Refunds In Progress Alert */}
                {refundsData?.refunds?.filter(r => ['pending', 'processing'].includes(r.status)).length > 0 && (
                  <div className="alert-banner info" onClick={() => setActiveTab('refunds')} style={{ cursor: 'pointer' }}>
                    <span className="alert-icon">↩️</span>
                    <div className="alert-content">
                      <h4>Refunds In Progress</h4>
                      <p>{refundsData.refunds.filter(r => ['pending', 'processing'].includes(r.status)).length} refund(s) are being processed</p>
                    </div>
                    <span className="alert-action">View Details →</span>
                  </div>
                )}

                {/* Cancelled Campaigns Alert */}
                {campaigns.cancelled.length > 0 && (
                  <div className="alert-banner warning" onClick={() => setActiveTab('cancelled')} style={{ cursor: 'pointer' }}>
                    <span className="alert-icon">🚫</span>
                    <div className="alert-content">
                      <h4>Cancelled Campaigns</h4>
                      <p>{campaigns.cancelled.length} campaign(s) have been cancelled</p>
                    </div>
                    <span className="alert-action">View →</span>
                  </div>
                )}

                {/* No Live Campaigns - Prompt to Create */}
                {campaigns.live.length === 0 && campaigns.draft.length === 0 && (
                  <div className="alert-banner success">
                    <span className="alert-icon">🚀</span>
                    <div className="alert-content">
                      <h4>Ready to Start?</h4>
                      <p>Create your first campaign and connect with creators</p>
                    </div>
                    <Link to="/create-campaign" className="alert-btn">Create Campaign</Link>
                  </div>
                )}
              </div>
              
              {/* Main Stats Grid */}
              <div className="stats-grid clickable">
                <div 
                  className="stat-card"
                  onClick={() => setActiveTab('blueprint')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">📈</span>
                  <div className="stat-info">
                    <span className="stat-value">{dashboardData?.overview?.total_campaigns || 0}</span>
                    <span className="stat-label">Total Campaigns</span>
                  </div>
                </div>
                <div 
                  className={`stat-card live ${campaigns.live.length > 0 ? 'highlight' : ''}`}
                  onClick={() => exitCampaignDetailToTab('live')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">🔴</span>
                  <div className="stat-info">
                    <span className="stat-value">{campaigns.live.length}</span>
                    <span className="stat-label">Live Campaigns</span>
                  </div>
                  {campaigns.live.length > 0 && <span className="click-hint">View →</span>}
                </div>
                <div 
                  className="stat-card"
                  onClick={() => setActiveTab('crm')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">👥</span>
                  <div className="stat-info">
                    <span className="stat-value">{dashboardData?.overview?.saved_creators || savedCreators.length || 0}</span>
                    <span className="stat-label">Saved Creators</span>
                  </div>
                </div>
                <div 
                  className="stat-card spent"
                  onClick={() => setActiveTab('funds')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">💰</span>
                  <div className="stat-info">
                    <span className="stat-value">{formatCurrency(fundsData?.overview?.total_spent || dashboardData?.overview?.total_spent || 0)}</span>
                    <span className="stat-label">Total Spent</span>
                  </div>
                </div>
              </div>

              {/* Secondary Stats */}
              <div className="secondary-stats">
                <div className="mini-stat" onClick={() => setActiveTab('blueprint')} style={{ cursor: 'pointer' }}>
                  <span className="mini-icon">📋</span>
                  <span className="mini-value">{campaigns.draft.length}</span>
                  <span className="mini-label">Drafts</span>
                </div>
                <div className="mini-stat" onClick={() => exitCampaignDetailToTab('completed')} style={{ cursor: 'pointer' }}>
                  <span className="mini-icon">✅</span>
                  <span className="mini-value">{campaigns.completed.length}</span>
                  <span className="mini-label">Completed</span>
                </div>
                <div
                  className="mini-stat"
                  onClick={() => setActiveTab('funds')}
                  style={{ cursor: 'pointer' }}
                  title="Money still in Milan's custody — future creator payouts and any pending refunds back to you. Excludes platform fees already earned."
                >
                  <span className="mini-icon">🔒</span>
                  <span className="mini-value">{formatCurrency(fundsData?.overview?.total_blocked || 0)}</span>
                  <span className="mini-label">Blocked</span>
                </div>
                <div className="mini-stat" onClick={() => setActiveTab('refunds')} style={{ cursor: 'pointer' }}>
                  <span className="mini-icon">↩️</span>
                  <span className="mini-value">{formatCurrency(refundsData?.summary?.total_refunded || 0)}</span>
                  <span className="mini-label">Refunded</span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="quick-actions-section">
                <h3>Quick Actions</h3>
                <div className="quick-actions-grid">
                  <Link to="/create-campaign" className="quick-action-btn">
                    <span className="qa-icon">➕</span>
                    <span className="qa-label">New Campaign</span>
                  </Link>
                  <Link to="/find-creators" className="quick-action-btn">
                    <span className="qa-icon">🔍</span>
                    <span className="qa-label">Find Creators</span>
                  </Link>
                  <button className="quick-action-btn" onClick={() => setActiveTab('crm')}>
                    <span className="qa-icon">👥</span>
                    <span className="qa-label">Creator CRM</span>
                  </button>
                  <button className="quick-action-btn" onClick={() => setActiveTab('funds')}>
                    <span className="qa-icon">💰</span>
                    <span className="qa-label">View Funds</span>
                  </button>
                </div>
              </div>

              {/* Dashboard Grid */}
              <div className="dashboard-sections">
                <section className="section-card">
                  <div className="section-header">
                    <h3>🔴 Live Campaigns</h3>
                    {campaigns.live.length > 0 && (
                      <button className="btn-view-all" onClick={() => exitCampaignDetailToTab('live')}>View All →</button>
                    )}
                  </div>
                  {campaigns.live.length === 0 ? (
                    <div className="empty-state-mini">
                      <span>📋</span>
                      <p>No live campaigns</p>
                      <Link to="/create-campaign" className="btn-small">Create Campaign</Link>
                    </div>
                  ) : (
                    <div className="campaign-list">
                      {campaigns.live.slice(0, 3).map(campaign => (
                        <div key={campaign.id} className="campaign-item">
                          <div className="campaign-info">
                            <h4>{campaign.name || campaign.campaign_name}</h4>
                            <span className="campaign-meta">
                              {campaign.selected_creators?.length || campaign.creator_count_expected || 0} creators • 
                              <span className={`status-mini ${campaign.status}`}>{campaign.status?.replace('_', ' ')}</span>
                            </span>
                          </div>
                          <span className="campaign-budget" title="Total funds blocked in escrow for this campaign">
                            {formatCurrency(
                              campaign.escrow_amount
                              ?? campaign.funds_blocked
                              ?? campaign.budget
                              ?? campaign.budget_max
                              ?? 0
                            )}
                          </span>
                          <button 
                            className="btn-view"
                            onClick={() => selectCampaign(campaign)}
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="section-card">
                  <div className="section-header">
                    <h3>📋 Draft Campaigns</h3>
                    {campaigns.draft.length > 0 && (
                      <button className="btn-view-all" onClick={() => setActiveTab('blueprint')}>View All →</button>
                    )}
                  </div>
                  {campaigns.draft.length === 0 ? (
                    <div className="empty-state-mini">
                      <span>✅</span>
                      <p>No drafts pending</p>
                    </div>
                  ) : (
                    <div className="draft-list-mini">
                      {campaigns.draft.slice(0, 3).map(campaign => (
                        <div key={campaign.id} className="draft-item-mini">
                          <div className="draft-info">
                            <h4>{campaign.name || campaign.campaign_name}</h4>
                            <span className="draft-meta">{formatCurrency(campaign.budget || campaign.budget_max || 0)}</span>
                          </div>
                          <button 
                            className="btn-continue"
                            onClick={() => navigate(`/create-campaign?id=${campaign.id}&step=2`)}
                          >
                            Continue →
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              {/* Financial Summary */}
              <section className="section-card financial-summary">
                <div className="section-header">
                  <h3>💰 Financial Summary</h3>
                  <button className="btn-view-all" onClick={() => setActiveTab('funds')}>View Details →</button>
                </div>
                <div className="financial-grid">
                  <div
                    className="financial-item"
                    title={
                      (fundsData?.overview?.total_platform_fees || 0) > 0
                        ? `Includes ${formatCurrency(fundsData?.overview?.total_platform_fees)} platform fee`
                        : 'Total paid into escrow for your campaigns (includes platform fee)'
                    }
                  >
                    <span className="financial-label">Total Escrow</span>
                    <span className="financial-value">
                      {formatCurrency(
                        fundsData?.overview?.total_escrow ||
                          fundsData?.overview?.total_blocked ||
                          0
                      )}
                    </span>
                  </div>
                  <div
                    className="financial-item"
                    title="Money still in Milan's custody — future creator payouts and any pending refunds back to you. Excludes platform fees already earned."
                  >
                    <span className="financial-label">Blocked (On Hold)</span>
                    <span className="financial-value yellow">{formatCurrency(fundsData?.overview?.total_blocked || 0)}</span>
                  </div>
                  <div className="financial-item">
                    <span className="financial-label">Total Spent</span>
                    <span className="financial-value green">{formatCurrency(fundsData?.overview?.total_spent || dashboardData?.overview?.total_spent || 0)}</span>
                  </div>
                  <div className="financial-item">
                    <span className="financial-label">Total Refunded</span>
                    <span className="financial-value blue">{formatCurrency(refundsData?.summary?.total_refunded || 0)}</span>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* Draft Campaigns Tab */}
          {activeTab === 'blueprint' && (
            <div className="tab-content">
              <div className="tab-header-row">
                <div>
                  <h1>Draft Campaigns</h1>
                  <p className="tab-subtitle">Manage your campaign drafts and pending campaigns</p>
                </div>
                <Link to="/create-campaign" className="btn-primary">
                  + Create New Campaign
                </Link>
              </div>

              {campaigns.draft.length === 0 ? (
                <div className="empty-state">
                  <span>📋</span>
                  <h3>No draft campaigns</h3>
                  <p>Start creating your first campaign to see AI-powered creator recommendations</p>
                </div>
              ) : (
                <div className="draft-campaigns">
                  <div className="campaigns-grid">
                    {campaigns.draft.map(campaign => (
                      <div key={campaign.id} className="draft-card">
                        <h4>{campaign.name || campaign.campaign_name}</h4>
                        <p>Budget: {formatCurrency(campaign.budget || campaign.budget_max)}</p>
                        <div className="draft-meta">
                          <span>{campaign.niche?.join(', ') || 'No niche'}</span>
                          <span>{campaign.creator_count_expected || 0} creators</span>
                        </div>
                        <div className="draft-actions">
                          <button 
                            className="btn-secondary"
                            onClick={() => navigate(`/create-campaign?id=${campaign.id}&step=1`)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-primary"
                            onClick={() => navigate(`/create-campaign?id=${campaign.id}&step=2`)}
                          >
                            Continue →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Live Campaigns Tab — also hosts the detail view when opening a completed campaign from Completed tab */}
          {(activeTab === 'live' || (activeTab === 'completed' && selectedCampaign)) && (
            <div className="tab-content">
              {activeTab === 'completed' && selectedCampaign ? (
                <>
                  <h1>Completed Campaigns</h1>
                  <p className="tab-description">Campaigns that have finished monitoring and payments are complete</p>
                </>
              ) : (
                <h1>Live Campaigns</h1>
              )}
              
              {/* On a hard refresh of /brand-dashboard?tab=live&campaign=<id>
                  the list view would render for a frame before the auto-select
                  effect picks up the URL param and swaps in the detail view.
                  Show a spinner in that gap so the user doesn't see the list
                  flash. */}
              {searchParams.get('campaign') && (!selectedCampaign || !campaignAnalytics) ? (
                <div className="dashboard-loading" style={{ padding: '60px 0' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading campaign...</p>
                </div>
              ) : selectedCampaign && campaignAnalytics ? (
                <div className="campaign-detail">
                  {/* Section 1: Campaign Header */}
                  <div className="campaign-header-section">
                    <button className="btn-back" onClick={() => { 
                      const backTab = selectedCampaign?.status === 'completed' ? 'completed' : 'live';
                      setSelectedCampaign(null); 
                      setCampaignDetails(null); 
                      setCampaignDetailsError(null);
                      setCampaignPerformance(null); 
                      setCreatorStatusFilter('pending_response');
                      setActiveTab(backTab);
                      navigate(`/brand-dashboard?tab=${backTab}`, { replace: true });
                    }}>
                      ← Back to all campaigns
                    </button>
                    <div className="campaign-title-row">
                      <div className="campaign-title-info">
                        <h2>{selectedCampaign.name || selectedCampaign.campaign_name}</h2>
                        <div className="campaign-meta">
                          <span className={`status-badge ${getStatusDisplay(selectedCampaign.status).class}`}>
                            {getStatusDisplay(selectedCampaign.status).label}
                          </span>
                          <span className="campaign-duration">
                            {selectedCampaign.start_date && selectedCampaign.end_date ? (
                              <>
                                {new Date(selectedCampaign.start_date).toLocaleDateString()} - {new Date(selectedCampaign.end_date).toLocaleDateString()}
                                {(() => {
                                  const endDate = new Date(selectedCampaign.end_date);
                                  const today = new Date();
                                  const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                                  return daysRemaining > 0 ? ` • ${daysRemaining} days remaining` : ' • Campaign ended';
                                })()}
                              </>
                            ) : 'Duration not set'}
                          </span>
                        </div>
                      </div>
                      {/* Cancel Campaign Button - only show for cancellable statuses */}
                      {['invitations_sent', 'in_progress'].includes(selectedCampaign.status) && (
                        <button 
                          className="btn-cancel-campaign"
                          onClick={() => {
                            setCampaignToCancel(selectedCampaign);
                            setCancelModalOpen(true);
                          }}
                        >
                          🗑️ Cancel Campaign
                        </button>
                      )}
                    </div>
                  </div>

                  {campaignDetailsError && (
                    <div className="alert-banner warning" style={{ marginBottom: '1rem' }}>
                      <span className="alert-icon">⚠️</span>
                      <div className="alert-content" style={{ flex: 1 }}>
                        <strong>Could not load creator pipeline and stats.</strong>
                        <p style={{ margin: '0.35rem 0 0', opacity: 0.95 }}>{campaignDetailsError}</p>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ marginTop: '0.65rem' }}
                          onClick={() =>
                            fetchCampaignAnalytics(selectedCampaign.id || selectedCampaign._id)
                          }
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Section 2: Quick Stats Bar */}
                  <div className="quick-stats-bar">
                    <div className="quick-stat">
                      <span className="quick-stat-icon">📧</span>
                      <div className="quick-stat-content">
                        <span className="quick-stat-value">{campaignDetails?.summary?.total_invitations || 0}</span>
                        <span className="quick-stat-label">Invited</span>
                      </div>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-stat-icon">⏳</span>
                      <div className="quick-stat-content">
                        <span className="quick-stat-value">{campaignDetails?.summary?.pending || 0}</span>
                        <span className="quick-stat-label">Pending</span>
                      </div>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-stat-icon">✅</span>
                      <div className="quick-stat-content">
                        <span className="quick-stat-value">{campaignDetails?.summary?.accepted || 0}</span>
                        <span className="quick-stat-label">Accepted</span>
                      </div>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-stat-icon">❌</span>
                      <div className="quick-stat-content">
                        <span className="quick-stat-value">{campaignDetails?.summary?.rejected || 0}</span>
                        <span className="quick-stat-label">Declined</span>
                      </div>
                    </div>
                    <div className="quick-stat">
                      <span className="quick-stat-icon">🕐</span>
                      <div className="quick-stat-content">
                        <span className="quick-stat-value">{campaignDetails?.summary?.on_standby || 0}</span>
                        <span className="quick-stat-label">On Standby</span>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Real-Time Performance Dashboard */}
                  <div className="performance-dashboard">
                    <div className="dashboard-header">
                      <h3>📊 Real-Time Performance</h3>
                      <button 
                        className="btn-refresh-small"
                        onClick={handleRefreshCampaignPerformance}
                        disabled={refreshingPerformance}
                      >
                        {refreshingPerformance ? '🔄 Refreshing...' : '🔄 Refresh'}
                      </button>
                    </div>
                    
                    {/* Real Data Notice */}
                    {campaignDetails?.real_time_performance?.has_real_data && (
                      <div className="real-data-notice">
                        <span className="live-indicator">🟢 Live</span>
                        <span>Showing real-time data from {campaignDetails.real_time_performance.creators_monitoring} creator(s) being monitored</span>
                      </div>
                    )}
                    {!campaignDetails?.real_time_performance?.has_real_data && (
                      <div className="no-data-notice">
                        <span className="placeholder-indicator">📊</span>
                        <span>Placeholder data - No creators have submitted published URLs yet</span>
                      </div>
                    )}
                    
                    <div className="performance-cards">
                      {/* Engagement Metrics Card */}
                      <div className="perf-card engagement">
                        <div className="perf-card-header">
                          <span className="perf-card-icon">📈</span>
                          <span className="perf-card-title">ENGAGEMENT</span>
                        </div>
                        <div className="perf-card-metrics">
                          <div className="perf-metric">
                            <span className="metric-icon">❤️</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? campaignDetails.real_time_performance.total_likes 
                                : 0)}
                            </span>
                            <span className="metric-label">Likes</span>
                          </div>
                          <div className="perf-metric">
                            <span className="metric-icon">💬</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? campaignDetails.real_time_performance.total_comments 
                                : 0)}
                            </span>
                            <span className="metric-label">Comments</span>
                          </div>
                          <div className="perf-metric">
                            <span className="metric-icon">📤</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? (campaignPerformance?.total_shares || 0) 
                                : 0)}
                            </span>
                            <span className="metric-label">Shares</span>
                          </div>
                          <div className="perf-metric">
                            <span className="metric-icon">🔖</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? (campaignPerformance?.total_saves || 0) 
                                : 0)}
                            </span>
                            <span className="metric-label">Saves</span>
                          </div>
                        </div>
                        <div className="perf-card-footer">
                          <span className="engagement-rate">📊 Engagement Rate: <strong>
                            {campaignDetails?.real_time_performance?.has_real_data 
                              ? (campaignDetails.real_time_performance.engagement_rate || campaignAnalytics.engagement_rate || 0) 
                              : 0}%
                          </strong></span>
                        </div>
                      </div>

                      {/* Reach & Views Card */}
                      <div className="perf-card reach">
                        <div className="perf-card-header">
                          <span className="perf-card-icon">👁️</span>
                          <span className="perf-card-title">REACH & VIEWS</span>
                        </div>
                        <div className="perf-card-metrics">
                          <div className="perf-metric">
                            <span className="metric-icon">📊</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? campaignDetails.real_time_performance.total_reach 
                                : 0)}
                            </span>
                            <span className="metric-label">Total Reach</span>
                          </div>
                          <div className="perf-metric">
                            <span className="metric-icon">👀</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? (campaignPerformance?.total_impressions || Math.round(campaignDetails.real_time_performance.total_reach * 1.4) || 0) 
                                : 0)}
                            </span>
                            <span className="metric-label">Impressions</span>
                          </div>
                          <div className="perf-metric">
                            <span className="metric-icon">🎬</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? campaignDetails.real_time_performance.total_views 
                                : 0)}
                            </span>
                            <span className="metric-label">Video Views</span>
                          </div>
                          <div className="perf-metric">
                            <span className="metric-icon">👤</span>
                            <span className="metric-value">
                              {formatNumber(campaignDetails?.real_time_performance?.has_real_data 
                                ? (campaignPerformance?.profile_visits || 0) 
                                : 0)}
                            </span>
                            <span className="metric-label">Profile Visits</span>
                          </div>
                        </div>
                      </div>

                      {/* Budget Card */}
                      <div className="perf-card budget">
                        <div className="perf-card-header">
                          <span className="perf-card-icon">💰</span>
                          <span className="perf-card-title">BUDGET</span>
                        </div>
                        <div className="budget-content">
                          <div className="budget-allocated">
                            <span>Escrow Blocked</span>
                            <strong className="escrow-amount">{formatCurrency(campaignDetails?.escrow_analysis?.escrow_amount || campaignDetails?.escrow?.amount || selectedCampaign.escrow_amount || selectedCampaign.budget || 0)}</strong>
                          </div>
                          <div className="budget-progress">
                            <div 
                              className="budget-bar" 
                              style={{width: `${(campaignDetails?.escrow_analysis?.escrow_amount || campaignDetails?.escrow?.amount || selectedCampaign.escrow_amount) ? Math.min(((campaignDetails?.escrow?.released_amount || 0) / (campaignDetails?.escrow_analysis?.escrow_amount || campaignDetails?.escrow?.amount || selectedCampaign.escrow_amount || 1)) * 100, 100) : 0}%`}}
                            ></div>
                          </div>
                          <div className="budget-metrics">
                            {(() => {
                              const escrowAnalysis = campaignDetails?.escrow_analysis;
                              const escrowAmount = escrowAnalysis?.escrow_amount || campaignDetails?.escrow?.amount || selectedCampaign?.escrow_amount || selectedCampaign?.budget || 0;
                              const platformFee = escrowAnalysis?.platform_fee || campaignDetails?.escrow?.platform_fee || Math.round(escrowAmount * 0.035);
                              const creatorPool = escrowAnalysis?.creator_pool || (escrowAmount - platformFee);
                              const activeAllocation = escrowAnalysis?.active_creator_allocation || 0;
                              const pendingAllocation = escrowAnalysis?.pending_creator_allocation || 0;
                              const killFeesPaid = escrowAnalysis?.kill_fees_paid || 0;
                              
                              return (
                                <>
                                  <div className="budget-metric">
                                    <span>Platform Fee</span>
                                    <strong>{formatCurrency(platformFee)}</strong>
                                  </div>
                                  <div className="budget-metric">
                                    <span>Creator Pool</span>
                                    <strong>{formatCurrency(creatorPool)}</strong>
                                  </div>
                                  {killFeesPaid > 0 && (
                                    <div className="budget-metric warning">
                                      <span>Kill Fees Paid</span>
                                      <strong className="kill-fee">-{formatCurrency(killFeesPaid)}</strong>
                                    </div>
                                  )}
                                  {activeAllocation > 0 && (
                                    <div className="budget-metric highlight">
                                      <span>Active Creator</span>
                                      <strong>{formatCurrency(activeAllocation)}</strong>
                                    </div>
                                  )}
                                  {pendingAllocation > 0 && (
                                    <div className="budget-metric pending">
                                      <span>Pending Response</span>
                                      <strong>{formatCurrency(pendingAllocation)}</strong>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          
                          {/* Expected Escrow Excess */}
                          {(() => {
                            const escrowAnalysis = campaignDetails?.escrow_analysis;
                            const expectedExcess = escrowAnalysis?.expected_escrow_excess || 0;
                            
                            if (expectedExcess > 0) {
                              return (
                                <div className="expected-refund-banner">
                                  <span className="refund-icon">💰</span>
                                  <div className="refund-info">
                                    <span className="refund-label">Expected Refund</span>
                                    <span className="refund-amount">+{formatCurrency(expectedExcess)}</span>
                                  </div>
                                  <span className="refund-hint">When campaign completes</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Sentiment Analysis */}
                    <div className="sentiment-card">
                      <div className="sentiment-header">
                        <span className="sentiment-icon">💭</span>
                        <span className="sentiment-title">CAMPAIGN SENTIMENT</span>
                      </div>
                      {campaignDetails?.real_time_performance?.has_real_data ? (
                        <>
                          <div className="sentiment-bar-container">
                            <div className="sentiment-bar-wrapper">
                              <div className="sentiment-segment positive" style={{width: `${campaignAnalytics.sentiment?.positive || 0}%`}}>
                                <span>😊 {campaignAnalytics.sentiment?.positive || 0}%</span>
                              </div>
                              <div className="sentiment-segment neutral" style={{width: `${campaignAnalytics.sentiment?.neutral || 0}%`}}>
                                <span>😐 {campaignAnalytics.sentiment?.neutral || 0}%</span>
                              </div>
                              <div className="sentiment-segment negative" style={{width: `${campaignAnalytics.sentiment?.negative || 0}%`}}>
                                <span>😟 {campaignAnalytics.sentiment?.negative || 0}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="sentiment-legend">
                            <span className="legend-item positive">😊 Positive</span>
                            <span className="legend-item neutral">😐 Neutral</span>
                            <span className="legend-item negative">😟 Negative</span>
                          </div>
                        </>
                      ) : (
                        <div className="sentiment-no-data">
                          <span>📊</span>
                          <p>Sentiment analysis will be available once creators submit content</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 4: Creator Pipeline */}
                  <div className="creator-pipeline-section">
                    <h3>👥 Creator Pipeline</h3>
                    
                    {/* 5 Tab Buttons */}
                    <div className="pipeline-tabs">
                      <button 
                        className={`pipeline-tab ${creatorStatusFilter === 'pending_response' ? 'active' : ''}`}
                        onClick={() => setCreatorStatusFilter('pending_response')}
                      >
                        <span className="tab-icon">📧</span>
                        <span className="tab-label">Pending Response</span>
                        <span className="tab-count">{campaignDetails?.creator_status?.pending_response?.length || 0}</span>
                      </button>
                      <button 
                        className={`pipeline-tab ${creatorStatusFilter === 'waiting' ? 'active' : ''}`}
                        onClick={() => setCreatorStatusFilter('waiting')}
                      >
                        <span className="tab-icon">📝</span>
                        <span className="tab-label">Waiting for Content</span>
                        <span className="tab-count">{campaignDetails?.creator_status?.waiting_for_submission?.length || 0}</span>
                      </button>
                      <button 
                        className={`pipeline-tab ${creatorStatusFilter === 'pending_review' ? 'active' : ''}`}
                        onClick={() => setCreatorStatusFilter('pending_review')}
                      >
                        <span className="tab-icon">⏳</span>
                        <span className="tab-label">Review Pending</span>
                        <span className="tab-count">{campaignDetails?.creator_status?.pending_review?.length || 0}</span>
                      </button>
                      <button 
                        className={`pipeline-tab ${creatorStatusFilter === 'approved' ? 'active' : ''}`}
                        onClick={() => setCreatorStatusFilter('approved')}
                      >
                        <span className="tab-icon">✅</span>
                        <span className="tab-label">Approved</span>
                        <span className="tab-count">{campaignDetails?.creator_status?.approved?.length || 0}</span>
                      </button>
                      <button 
                        className={`pipeline-tab ${creatorStatusFilter === 'monitoring' ? 'active' : ''}`}
                        onClick={() => setCreatorStatusFilter('monitoring')}
                      >
                        <span className="tab-icon">📊</span>
                        <span className="tab-label">Monitoring</span>
                        <span className="tab-count">{campaignDetails?.creator_status?.monitoring?.length || 0}</span>
                      </button>
                      <button 
                        className={`pipeline-tab ${creatorStatusFilter === 'rejected' ? 'active' : ''}`}
                        onClick={() => setCreatorStatusFilter('rejected')}
                      >
                        <span className="tab-icon">❌</span>
                        <span className="tab-label">Rejected/Cancelled</span>
                        <span className="tab-count">{(campaignDetails?.creator_status?.rejected?.length || 0) + (campaignDetails?.creator_status?.cancelled?.length || 0)}</span>
                      </button>
                    </div>

                    {/* Pipeline Content */}
                    <div className="pipeline-content">
                      {/* Pending Response Tab */}
                      {creatorStatusFilter === 'pending_response' && (
                        <div className="pipeline-tab-content">
                          <div className="tab-content-header">
                            <p className="section-note">Creators who have been invited but haven't responded yet</p>
                            {campaignDetails?.creator_status?.pending_response?.length > 0 && (
                              <button
                                className="btn-resend-all"
                                onClick={() => handleSendReminders(selectedCampaign.id)}
                                disabled={resendingEmails}
                              >
                                {resendingEmails ? '📨 Sending...' : '🔔 Send Reminder'}
                              </button>
                            )}
                          </div>
                          {/* Help note: creators occasionally don't see the email because
                              their mail server filtered it. Give the brand a clear next step
                              before they panic that the system is broken. */}
                          {campaignDetails?.creator_status?.pending_response?.length > 0 && (
                            <div
                              style={{
                                background: '#fffbeb',
                                border: '1px solid #fde68a',
                                color: '#78350f',
                                padding: '10px 12px',
                                borderRadius: 8,
                                fontSize: 13,
                                marginBottom: 12,
                              }}
                            >
                              <strong>Creator says they didn't get the email?</strong>
                              {' '}First ask them to check their <em>spam / promotions</em> folder.
                              Then use <strong>📧 Resend email</strong> below to send it again to that one creator.
                              The address shown next to each name is where we'll deliver it.
                            </div>
                          )}
                          {campaignDetails?.creator_status?.pending_response?.length > 0 ? (
                            <div className="creators-status-list">
                              {campaignDetails.creator_status.pending_response.map(inv => (
                                <div key={inv.id} className="creator-status-card pending-response">
                                  <div className="creator-info">
                                    <div className="creator-avatar">
                                      {inv.creator?.profile_picture_url ? (
                                        <img src={inv.creator.profile_picture_url} alt="" />
                                      ) : (
                                        <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                      )}
                                    </div>
                                    <div className="creator-details">
                                      <strong>{inv.creator_name}</strong>
                                      <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                      <span className="followers">{formatNumber(inv.creator?.instagram_followers_count || 0)} followers</span>
                                      {/* Surface the registered email so the brand can verify
                                          where the invitation was actually sent. Useful when a
                                          creator says "I never got the email" — often the
                                          inbox they're checking isn't the one we have on file. */}
                                      {inv.creator?.email && (
                                        <span className="recipient-email" style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                          ✉ {inv.creator.email}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="status-info">
                                    <span className="status-badge pending-response">
                                      {inv.email_sent ? '📧 Email Sent' : '📭 Email Not Sent'}
                                    </span>
                                    <span className="payment">{formatCurrency(inv.base_payment || 0)}</span>
                                    {inv.email_sent_at && (
                                      <span className="sent-time">Sent: {new Date(inv.email_sent_at).toLocaleDateString()}</span>
                                    )}
                                    <button
                                      type="button"
                                      className="btn-resend-one"
                                      style={{
                                        marginTop: 6,
                                        padding: '6px 10px',
                                        fontSize: 12,
                                        background: '#eef2ff',
                                        border: '1px solid #c7d2fe',
                                        color: '#4338ca',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                      }}
                                      onClick={() => handleResendOneInvitation(selectedCampaign.id, inv.id)}
                                      disabled={!!resendingInviteIds[inv.id]}
                                      title="Send the invitation email to this creator again"
                                    >
                                      {resendingInviteIds[inv.id] ? 'Sending…' : '📧 Resend email'}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">
                              <span className="empty-icon">✅</span>
                              <p>All invited creators have responded</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Waiting for Content Tab */}
                      {creatorStatusFilter === 'waiting' && (
                        <div className="pipeline-tab-content">
                          <p className="section-note">Creators who accepted the campaign but haven't submitted content yet</p>
                          {campaignDetails?.creator_status?.waiting_for_submission?.length > 0 ? (
                            <div className="creators-status-list">
                              {campaignDetails.creator_status.waiting_for_submission.map(inv => (
                                <div key={inv.id} className="creator-status-card waiting">
                                  <div className="creator-info">
                                    <div className="creator-avatar">
                                      <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                    </div>
                                    <div className="creator-details">
                                      <strong>{inv.creator_name}</strong>
                                      <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                    </div>
                                  </div>
                                  <div className="status-info">
                                    <span className="status-badge waiting">
                                      {inv.task_status === 'revision_needed' ? '🔄 Revision Needed' : '📝 Not Submitted'}
                                    </span>
                                    <span className="payment">{formatCurrency(inv.base_payment || 0)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">
                              <span className="empty-icon">📭</span>
                              <p>No creators waiting for content submission</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Pending Review Tab */}
                      {creatorStatusFilter === 'pending_review' && (
                        <div className="pipeline-tab-content">
                          <p className="section-note">Content submitted by creators awaiting your review</p>
                          {campaignDetails?.creator_status?.pending_review?.length > 0 ? (
                            <div className="creators-status-list">
                              {campaignDetails.creator_status.pending_review.map(inv => (
                                <div key={inv.id} className="creator-review-card">
                                  <div className="review-card-header">
                                    <div className="creator-info">
                                      <div className="creator-avatar">
                                        <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                      </div>
                                      <div className="creator-details">
                                        <strong>{inv.creator_name}</strong>
                                        <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                      </div>
                                    </div>
                                    <div className="submission-meta">
                                      <span className="submission-number">Submission #{inv.submission_number || inv.submission_count}</span>
                                      <span className="submission-date">{inv.submitted_at ? new Date(inv.submitted_at).toLocaleString() : ''}</span>
                                    </div>
                                  </div>
                                  
                                  {inv.content_url && (
                                    <div className="content-preview">
                                      <a href={inv.content_url} target="_blank" rel="noopener noreferrer" className="content-link">
                                        📎 View Submitted Content
                                      </a>
                                      {inv.submission_notes && (
                                        <p className="submission-notes"><strong>Notes:</strong> {inv.submission_notes}</p>
                                      )}
                                    </div>
                                  )}
                                  
                                  <div className="review-actions">
                                    {inv.submission_count >= (inv.max_submissions || 3) && (
                                      <div className="final-submission-notice">
                                        ⚠️ Final submission ({inv.submission_count}/{inv.max_submissions}). You can only Approve or Cancel.
                                      </div>
                                    )}
                                    
                                    <button 
                                      className="btn-approve"
                                      onClick={() => handleReviewSubmission(inv.submission_id, 'approve')}
                                      disabled={reviewingSubmission === inv.submission_id || !inv.submission_id}
                                    >
                                      ✅ Approve
                                    </button>
                                    {!inv.submission_id && (
                                      <span className="no-submission-warning">⚠️ No submission found</span>
                                    )}
                                    
                                    {inv.submission_count < (inv.max_submissions || 3) && (
                                      <button 
                                        className="btn-revision"
                                        onClick={() => {
                                          const feedback = prompt('Provide feedback for revision:');
                                          if (feedback) handleReviewSubmission(inv.submission_id, 'request_revision', feedback);
                                        }}
                                        disabled={reviewingSubmission === inv.submission_id}
                                      >
                                        🔄 Request Revision
                                      </button>
                                    )}
                                    
                                    {inv.submission_count >= (inv.max_submissions || 3) && (
                                      <button 
                                        className="btn-cancel-campaign"
                                        onClick={() => {
                                          const feedback = prompt('Reason for cancellation (8.5% kill fee will be deducted from this creator\'s fixed payment):');
                                          if (feedback && window.confirm('Are you sure? An 8.5% kill fee will be deducted from this creator\'s fixed payment in your escrow.')) {
                                            handleReviewSubmission(inv.submission_id, 'cancel_campaign', feedback);
                                          }
                                        }}
                                        disabled={reviewingSubmission === inv.submission_id}
                                      >
                                        🚫 Cancel (8.5% Kill Fee)
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">
                              <span className="empty-icon">📭</span>
                              <p>No content submissions pending review</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Approved Tab */}
                      {creatorStatusFilter === 'approved' && (
                        <div className="pipeline-tab-content">
                          <p className="section-note">Creators whose content has been approved but not yet published on Instagram</p>
                          {campaignDetails?.creator_status?.approved?.length > 0 ? (
                            <div className="creators-status-list">
                              {campaignDetails.creator_status.approved.map(inv => (
                                <div key={inv.id} className="creator-status-card approved">
                                  <div className="creator-info">
                                    <div className="creator-avatar">
                                      <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                    </div>
                                    <div className="creator-details">
                                      <strong>{inv.creator_name}</strong>
                                      <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                    </div>
                                  </div>
                                  <div className="status-info">
                                    <span className="status-badge approved">✅ Approved</span>
                                    <span className="payment">{formatCurrency(inv.base_payment || 0)}</span>
                                  </div>
                                  {inv.content_url && (
                                    <a href={inv.content_url} target="_blank" rel="noopener noreferrer" className="content-link-small">
                                      📎 View Content
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">
                              <span className="empty-icon">📭</span>
                              <p>No approved content yet</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Monitoring Tab - Creators who submitted published Instagram URL */}
                      {creatorStatusFilter === 'monitoring' && (
                        <div className="pipeline-tab-content">
                          <p className="section-note">Creators who have published content on Instagram and are being monitored</p>
                          {campaignDetails?.creator_status?.monitoring?.length > 0 ? (
                            <div className="creators-status-list">
                              {campaignDetails.creator_status.monitoring.map(inv => (
                                <div key={inv.id} className="creator-monitoring-card">
                                  <div className="monitoring-card-header">
                                    <div className="creator-info">
                                      <div className="creator-avatar monitoring">
                                        <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                      </div>
                                      <div className="creator-details">
                                        <strong>{inv.creator_name}</strong>
                                        <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                      </div>
                                    </div>
                                    <div className="monitoring-status">
                                      <span className="status-badge monitoring">
                                        📊 Day {inv.days_completed || 0}/7
                                      </span>
                                      <span className="tracking-status">
                                        {inv.tracking_status === 'monitoring' ? '🟢 Active' : '✅ Completed'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Instagram Post Link */}
                                  {inv.platform_url && (
                                    <div className="instagram-post-link">
                                      <a href={inv.platform_url} target="_blank" rel="noopener noreferrer">
                                        📱 View Instagram Post →
                                      </a>
                                    </div>
                                  )}
                                  
                                  {/* Live Metrics */}
                                  <div className="monitoring-metrics">
                                    <div className="monitoring-metric">
                                      <span className="metric-icon">❤️</span>
                                      <span className="metric-value">{formatNumber(inv.current_likes || 0)}</span>
                                      <span className="metric-label">Likes</span>
                                    </div>
                                    <div className="monitoring-metric">
                                      <span className="metric-icon">💬</span>
                                      <span className="metric-value">{formatNumber(inv.current_comments || 0)}</span>
                                      <span className="metric-label">Comments</span>
                                    </div>
                                    <div className="monitoring-metric">
                                      <span className="metric-icon">👁️</span>
                                      <span className="metric-value">{formatNumber(inv.current_views || 0)}</span>
                                      <span className="metric-label">Views</span>
                                    </div>
                                    <div className="monitoring-metric">
                                      <span className="metric-icon">📊</span>
                                      <span className="metric-value">{formatNumber(inv.current_reach || 0)}</span>
                                      <span className="metric-label">Reach</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">
                              <span className="empty-icon">📊</span>
                              <p>No creators being monitored yet</p>
                              <p className="empty-note">Creators will appear here after they publish approved content and submit the Instagram post URL</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Rejected/Cancelled Tab */}
                      {creatorStatusFilter === 'rejected' && (
                        <div className="pipeline-tab-content">
                          <p className="section-note">Creators whose content was rejected or cancelled after maximum submission attempts</p>
                          
                          {/* Cancelled Creators */}
                          {campaignDetails?.creator_status?.cancelled?.length > 0 && (
                            <div className="rejected-subsection">
                              <h4>🚫 Cancelled (8.5% Kill Fee Applied)</h4>
                              <div className="creators-status-list">
                                {campaignDetails.creator_status.cancelled.map(inv => (
                                  <div key={inv.id} className="creator-status-card cancelled">
                                    <div className="creator-info">
                                      <div className="creator-avatar">
                                        <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                      </div>
                                      <div className="creator-details">
                                        <strong>{inv.creator_name}</strong>
                                        <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                      </div>
                                    </div>
                                    <div className="status-info">
                                      <span className="status-badge cancelled">🚫 Cancelled</span>
                                      <span className="kill-fee" title="8.5% of this creator's fixed payment, deducted from your escrow at cancellation">
                                        Kill Fee: {formatCurrency(inv.kill_fee || 0)}
                                      </span>
                                      <span className="submissions-info">{inv.submission_count}/{inv.max_submissions} submissions</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Rejected Creators */}
                          {campaignDetails?.creator_status?.rejected?.length > 0 && (
                            <div className="rejected-subsection">
                              <h4>❌ Rejected</h4>
                              <div className="creators-status-list">
                                {campaignDetails.creator_status.rejected.map(inv => (
                                  <div key={inv.id} className="creator-status-card rejected">
                                    <div className="creator-info">
                                      <div className="creator-avatar">
                                        <span>{inv.creator_name?.charAt(0) || 'C'}</span>
                                      </div>
                                      <div className="creator-details">
                                        <strong>{inv.creator_name}</strong>
                                        <span className="username">@{inv.creator?.instagram_username || 'unknown'}</span>
                                      </div>
                                    </div>
                                    <div className="status-info">
                                      <span className="status-badge rejected">❌ Rejected</span>
                                      <span className="submissions-info">{inv.submission_count}/{inv.max_submissions} submissions</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Empty state for both */}
                          {(!campaignDetails?.creator_status?.cancelled?.length && !campaignDetails?.creator_status?.rejected?.length) && (
                            <div className="empty-state">
                              <span className="empty-icon">📭</span>
                              <p>No rejected or cancelled creators</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              ) : activeTab === 'live' ? (
                <div className="campaigns-list">
                  {campaigns.live.length === 0 ? (
                    <div className="empty-state">
                      <span>🎯</span>
                      <h3>No live campaigns</h3>
                      <p>Your active campaigns will appear here</p>
                    </div>
                  ) : (
                    campaigns.live.map(campaign => (
                      <div key={campaign.id} className="live-campaign-card" onClick={() => selectCampaign(campaign)}>
                        <div className="campaign-header">
                          <h3>{campaign.name || campaign.campaign_name}</h3>
                          <span className={`status-badge ${getStatusDisplay(campaign.status).class}`}>
                            {getStatusDisplay(campaign.status).label}
                          </span>
                        </div>
                        <div className="campaign-stats">
                          <div className="stat">
                            <span
                              className="label"
                              title="Total funds blocked in escrow (budget allocated to creators + 3.5% platform fee)"
                            >
                              Funds Blocked
                            </span>
                            <span className="value">
                              {formatCurrency(
                                campaign.escrow_amount
                                ?? campaign.funds_blocked
                                ?? campaign.budget
                                ?? campaign.budget_max
                                ?? 0
                              )}
                            </span>
                          </div>
                          <div className="stat">
                            <span className="label">Creators</span>
                            <span className="value">{campaign.selected_creators?.length || campaign.creator_count_expected || 0}</span>
                          </div>
                        </div>
                        <button className="btn-view-details">View Details →</button>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Completed Campaigns Tab */}
          {activeTab === 'completed' && !selectedCampaign && (
            <div className="tab-content">
              <h1>Completed Campaigns</h1>
              <p className="tab-description">Campaigns that have finished monitoring and payments are complete</p>

              <div className="campaigns-list completed-list">
                {campaigns.completed.length === 0 ? (
                  <div className="empty-state">
                    <span>📊</span>
                    <h3>No completed campaigns yet</h3>
                    <p>Completed campaigns will appear here after monitoring ends</p>
                  </div>
                ) : (
                  campaigns.completed.map(campaign => {
                    const financialSummary = campaign.financial_summary;
                    const escrowExcess = financialSummary?.escrow_excess_refunded || 0;
                    const relatedRefunds = refundsData?.refunds?.filter(r => 
                      r.campaign_id === (campaign.id || campaign._id) && 
                      (r.type === 'escrow_excess' || r.type === 'unused_performance')
                    ) || [];
                    const totalRefunded = relatedRefunds.reduce((sum, r) => sum + (r.amount || 0), 0);
                    
                    return (
                      <div key={campaign.id || campaign._id} className="completed-campaign-card">
                        <div className="campaign-header">
                          <div className="campaign-title">
                            <h3>{campaign.name || campaign.campaign_name}</h3>
                            <span className="completed-date">
                              Completed: {campaign.completed_at ? new Date(campaign.completed_at).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                          <span className="status-badge completed">✅ Completed</span>
                        </div>
                        
                        <div className="campaign-financial-summary">
                          <div className="financial-row">
                            <div className="financial-item">
                              <span className="label">Escrow Blocked</span>
                              <span className="value">{formatCurrency(campaign.escrow_amount || financialSummary?.escrow_amount || 0)}</span>
                            </div>
                            <div className="financial-item">
                              <span className="label">Platform Fee</span>
                              <span className="value muted">-{formatCurrency(financialSummary?.platform_fee || (campaign.escrow_amount * (0.035 / 1.035)) || 0)}</span>
                            </div>
                            <div className="financial-item">
                              <span className="label">Paid to Creators</span>
                              <span className="value green">{formatCurrency(financialSummary?.total_paid_to_creators || 0)}</span>
                            </div>
                            {(escrowExcess > 0 || totalRefunded > 0) && (
                              <div className="financial-item highlight">
                                <span className="label">Escrow Refund</span>
                                <span className="value refund">+{formatCurrency(escrowExcess || totalRefunded)}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Show refund details if any */}
                          {relatedRefunds.length > 0 && (
                            <div className="refund-details-section">
                              <span className="refund-tag">
                                💰 {relatedRefunds.length} refund(s) - {formatCurrency(totalRefunded)} returned
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="campaign-stats">
                          <div className="stat">
                            <span className="label">Creators</span>
                            <span className="value">{campaign.selected_creators?.length || 0}</span>
                          </div>
                          <div className="stat">
                            <span className="label">Net Cost</span>
                            <span className="value">{formatCurrency(
                              (financialSummary?.platform_fee || 0) + 
                              (financialSummary?.total_paid_to_creators || 0)
                            )}</span>
                          </div>
                        </div>
                        
                        <button 
                          className="btn-view-details"
                          onClick={() => selectCampaign(campaign)}
                        >
                          View Report →
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Creator CRM Tab */}
          {activeTab === 'crm' && (
            <div className="tab-content">
              <h1>Creator CRM</h1>
              <p className="tab-subtitle">Manage your saved creators and past collaborations</p>
              
              {/* CRM Navigation - 4 Parallel Buttons */}
              <div className="crm-nav-buttons">
                <button 
                  className={`crm-nav-btn ${crmView === 'saved' ? 'active' : ''}`}
                  onClick={() => setCrmView('saved')}
                >
                  <span className="nav-icon">📚</span>
                  <span className="nav-label">Saved Creators</span>
                  <span className="nav-count">{savedCreators.length}</span>
                </button>
                <button 
                  className={`crm-nav-btn ${crmView === 'campaign' ? 'active' : ''}`}
                  onClick={() => setCrmView('campaign')}
                >
                  <span className="nav-icon">🎯</span>
                  <span className="nav-label">From Campaigns</span>
                  <span className="nav-count">{campaignCreators.length}</span>
                </button>
                <button 
                  className={`crm-nav-btn ${crmView === 'search' ? 'active' : ''}`}
                  onClick={() => setCrmView('search')}
                >
                  <span className="nav-icon">🔍</span>
                  <span className="nav-label">Search & Add</span>
                  <span className="nav-arrow">+</span>
                </button>
              </div>

              {/* Saved Creators View */}
              {crmView === 'saved' && (
                <>
                  <div className="creators-grid">
                    {savedCreators.length === 0 ? (
                      <div className="empty-state">
                        <span>👥</span>
                        <h3>No saved creators</h3>
                        <p>Save creators from the Find Creators page or add from your campaigns</p>
                      </div>
                    ) : (
                      savedCreators.map(creator => (
                        <div key={creator.id} className="crm-creator-card enhanced">
                          <div className="creator-header">
                            <div className="creator-avatar">
                              {creator.instagram_profile_picture ? (
                                <img src={creator.instagram_profile_picture} alt={creator.name} />
                              ) : (
                                creator.name?.charAt(0) || 'C'
                              )}
                            </div>
                            <div className="creator-info">
                              <h4>{creator.name || creator.instagram_name}</h4>
                              <span className="username">@{creator.instagram_username || 'unknown'}</span>
                            </div>
                          </div>
                          
                          <div className="creator-stats-row">
                            <div className="stat">
                              <span className="value">{formatNumber(creator.instagram_followers_count || creator.followers_count || 0)}</span>
                              <span className="label">Followers</span>
                            </div>
                            <div className="stat">
                              <span className="value">{(creator.instagram_engagement_rate || 0).toFixed(2)}%</span>
                              <span className="label">Engagement</span>
                            </div>
                            <div className="stat">
                              <span className="value">{formatNumber(creator.instagram_media_count || 0)}</span>
                              <span className="label">Posts</span>
                            </div>
                          </div>

                          {creator.instagram_detected_niches?.length > 0 && (
                            <div className="creator-niches">
                              {creator.instagram_detected_niches.slice(0, 4).map(niche => (
                                <span key={niche} className="niche-tag">{niche}</span>
                              ))}
                            </div>
                          )}

                          <div className="tags">
                            {(creator.crm_data?.tags || creator.tags || []).map(tag => (
                              <span key={tag} className="tag">{tag}</span>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Campaign Creators View */}
              {crmView === 'campaign' && (
                <>
                  <div className="crm-info-banner">
                    <span>💡</span>
                    <p>Add creators who have worked on your campaigns to your CRM. See their real performance data.</p>
                  </div>

                  <div className="campaign-creators-list">
                    {campaignCreators.length === 0 ? (
                      <div className="empty-state">
                        <span>🎯</span>
                        <h3>No campaign creators yet</h3>
                        <p>Creators who accept your campaign invitations will appear here with their performance data</p>
                        <Link to="/create-campaign" className="btn-primary">Create a Campaign</Link>
                      </div>
                    ) : (
                      campaignCreators.map(creator => (
                        <div key={creator.id} className={`campaign-creator-card ${creator.is_saved ? 'saved' : ''}`}>
                          <div className="creator-main">
                            <div className="creator-avatar large">
                              {creator.instagram_profile_picture ? (
                                <img src={creator.instagram_profile_picture} alt={creator.name} />
                              ) : (
                                creator.name?.charAt(0) || 'C'
                              )}
                            </div>
                            <div className="creator-details">
                              <h4>{creator.name || creator.instagram_name}</h4>
                              <span className="username">@{creator.instagram_username}</span>
                              <span className="followers">{formatNumber(creator.instagram_followers_count)} followers</span>
                              {creator.instagram_detected_niches?.length > 0 && (
                                <div className="niches">
                                  {creator.instagram_detected_niches.slice(0, 3).map(niche => (
                                    <span key={niche} className="niche-tag">{niche}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="creator-stats">
                            <div className="stat-item">
                              <span className="stat-value">{creator.total_campaigns}</span>
                              <span className="stat-label">Campaigns</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{formatCurrency(creator.total_earned)}</span>
                              <span className="stat-label">Total Paid</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{creator.avg_engagement_rate}%</span>
                              <span className="stat-label">Avg. Engagement</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{formatNumber(creator.total_likes)}</span>
                              <span className="stat-label">Total Likes</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{formatNumber(creator.total_comments)}</span>
                              <span className="stat-label">Total Comments</span>
                            </div>
                          </div>

                          <div className="creator-campaigns">
                            <h5>Campaign History</h5>
                            <div className="campaigns-list">
                              {creator.campaigns?.map((camp, idx) => (
                                <div key={idx} className="mini-campaign">
                                  <span className="camp-name">{camp.campaign_name}</span>
                                  <span className={`camp-status ${camp.campaign_status}`}>{camp.campaign_status.replace('_', ' ')}</span>
                                  <span className="camp-payment">{formatCurrency(camp.payment)}</span>
                                  <div className="camp-metrics">
                                    <span>❤️ {formatNumber(camp.likes)}</span>
                                    <span>💬 {formatNumber(camp.comments)}</span>
                                    <span>📈 {camp.engagement_rate}%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="creator-actions">
                            {creator.is_saved ? (
                              <button className="btn-saved" disabled>
                                ✓ Saved to CRM
                              </button>
                            ) : (
                              <button 
                                className="btn-primary"
                                onClick={() => handleSaveCreatorToCRM(creator)}
                                disabled={savingCreator === creator.id}
                              >
                                {savingCreator === creator.id ? 'Saving...' : '+ Add to CRM'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Search & Add View */}
              {crmView === 'search' && (
                <>
                  <div className="crm-info-banner">
                    <span>🔍</span>
                    <p>Search for creators by name, username, or niche and add them directly to your CRM.</p>
                  </div>

                  <div className="crm-search-section">
                    <div className="search-bar-row">
                      <div className="search-input-wrapper">
                        <input
                          type="text"
                          placeholder="Search by name or @username..."
                          value={crmSearchQuery}
                          onChange={(e) => setCrmSearchQuery(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleCrmSearch()}
                          className="crm-search-input"
                        />
                      </div>
                      <button 
                        className="btn-primary search-btn"
                        onClick={handleCrmSearch}
                        disabled={crmSearchLoading}
                      >
                        {crmSearchLoading ? 'Searching...' : '🔍 Search'}
                      </button>
                    </div>

                    <div className="search-filters-row">
                      <select
                        value={crmSearchFilters.niche}
                        onChange={(e) => setCrmSearchFilters(prev => ({ ...prev, niche: e.target.value }))}
                        className="filter-select"
                      >
                        <option value="">All Niches</option>
                        <option value="Fashion">Fashion</option>
                        <option value="Tech">Tech</option>
                        <option value="Travel">Travel</option>
                        <option value="Food">Food</option>
                        <option value="Fitness">Fitness</option>
                        <option value="Beauty">Beauty</option>
                        <option value="Lifestyle">Lifestyle</option>
                        <option value="Gaming">Gaming</option>
                        <option value="Education">Education</option>
                        <option value="Finance">Finance</option>
                      </select>
                      <input
                        type="number"
                        placeholder="Min Followers"
                        value={crmSearchFilters.minFollowers}
                        onChange={(e) => setCrmSearchFilters(prev => ({ ...prev, minFollowers: e.target.value }))}
                        className="filter-input"
                      />
                      <input
                        type="number"
                        placeholder="Max Followers"
                        value={crmSearchFilters.maxFollowers}
                        onChange={(e) => setCrmSearchFilters(prev => ({ ...prev, maxFollowers: e.target.value }))}
                        className="filter-input"
                      />
                      <button 
                        className="btn-secondary clear-filters-btn"
                        onClick={() => {
                          setCrmSearchQuery('');
                          setCrmSearchFilters({ niche: '', minFollowers: '', maxFollowers: '' });
                          setCrmSearchResults([]);
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="crm-search-results">
                    {crmSearchLoading ? (
                      <div className="search-loading">
                        <div className="loading-spinner"></div>
                        <p>Searching creators...</p>
                      </div>
                    ) : crmSearchResults.length === 0 ? (
                      <div className="empty-state">
                        <span>🔍</span>
                        <h3>Search for creators</h3>
                        <p>Enter a name, username, or select filters to find creators</p>
                      </div>
                    ) : (
                      <>
                        <div className="results-count">
                          Found {crmSearchResults.length} creator{crmSearchResults.length !== 1 ? 's' : ''}
                        </div>
                        <div className="creators-grid search-results-grid">
                          {crmSearchResults.map(creator => (
                            <div key={creator.id} className={`crm-creator-card enhanced ${creator.is_saved ? 'saved' : ''}`}>
                              <div className="creator-header">
                                <div className="creator-avatar">
                                  {creator.instagram_profile_picture ? (
                                    <img src={creator.instagram_profile_picture} alt={creator.name} />
                                  ) : (
                                    creator.name?.charAt(0) || 'C'
                                  )}
                                </div>
                                <div className="creator-info">
                                  <h4>{creator.name || creator.instagram_name}</h4>
                                  <span className="username">@{creator.instagram_username || 'unknown'}</span>
                                </div>
                                {creator.is_saved && (
                                  <span className="saved-badge">✓ Saved</span>
                                )}
                              </div>
                              
                              <div className="creator-stats-row">
                                <div className="stat">
                                  <span className="value">{formatNumber(creator.instagram_followers_count || 0)}</span>
                                  <span className="label">Followers</span>
                                </div>
                                <div className="stat">
                                  <span className="value">{(creator.instagram_engagement_rate || 0).toFixed(2)}%</span>
                                  <span className="label">Engagement</span>
                                </div>
                                <div className="stat">
                                  <span className="value">{formatNumber(creator.instagram_media_count || 0)}</span>
                                  <span className="label">Posts</span>
                                </div>
                              </div>

                              {creator.instagram_detected_niches?.length > 0 && (
                                <div className="creator-niches">
                                  {creator.instagram_detected_niches.slice(0, 4).map(niche => (
                                    <span key={niche} className="niche-tag">{niche}</span>
                                  ))}
                                </div>
                              )}

                              <div className="creator-actions">
                                {creator.is_saved ? (
                                  <button className="btn-saved" disabled>
                                    ✓ Already in CRM
                                  </button>
                                ) : (
                                  <button 
                                    className="btn-primary"
                                    onClick={() => handleSaveSearchCreatorToCRM(creator)}
                                    disabled={savingCreator === creator.id}
                                  >
                                    {savingCreator === creator.id ? 'Saving...' : '+ Add to CRM'}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Campaign Funds Tab */}
          {activeTab === 'funds' && (
            <div className="tab-content">
              <h1>Campaign Funds</h1>
              <p className="tab-description">Real-time overview of your campaign budgets, escrow, and spending</p>
              
              {/* Primary Stats */}
              {(() => {
                const totalEscrow = fundsData?.overview?.total_escrow || fundsData?.overview?.total_blocked || 0;
                const totalPlatformFees = fundsData?.overview?.total_platform_fees || 0;
                const totalReleased = fundsData?.overview?.total_released || fundsData?.overview?.total_spent || 0;
                const pendingRefunds = fundsData?.overview?.pending_refunds || 0;
                const expectedRefunds = fundsData?.overview?.expected_refunds || 0;
                const totalRefunded = fundsData?.overview?.total_refunded || refundsData?.summary?.total_refunded || 0;
                const feePct = totalEscrow > 0 ? ((totalPlatformFees / totalEscrow) * 100).toFixed(1) : '0';
                return (
                  <>
                    {/* Primary stats: escrow, released, refunded. Platform fees in escrow subtext / tooltip. */}
                    <div className="funds-overview">
                      <div
                        className="fund-card escrow"
                        title={totalPlatformFees > 0
                          ? `Includes ${formatCurrency(totalPlatformFees)} platform fee (${feePct}%)`
                          : `Platform fee is 3.5% of escrow`}
                      >
                        <span className="fund-icon">🔒</span>
                        <div className="fund-info">
                          <span className="fund-label">Total Escrow</span>
                          <span className="fund-value">{formatCurrency(totalEscrow)}</span>
                          {totalPlatformFees > 0 && (
                            <span className="fund-sub">
                              incl. {formatCurrency(totalPlatformFees)} platform fee
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="fund-card released">
                        <span className="fund-icon">✅</span>
                        <div className="fund-info">
                          <span className="fund-label">Released from Escrow</span>
                          <span className="fund-value green">{formatCurrency(totalReleased)}</span>
                          {totalReleased > 0 && (
                            <span className="fund-sub">Paid out to creators by Milan AI</span>
                          )}
                        </div>
                      </div>
                      <div className="fund-card refunds">
                        <span className="fund-icon">↩️</span>
                        <div className="fund-info">
                          <span className="fund-label">Total Refunded</span>
                          <span className="fund-value blue">{formatCurrency(totalRefunded)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Refund status — only surfaced when something is in flight */}
                    {(pendingRefunds > 0 || expectedRefunds > 0) && (
                      <div className="funds-breakdown">
                        <h3>↩️ Refund Status</h3>
                        <div className="breakdown-grid">
                          {pendingRefunds > 0 && (
                            <div className="breakdown-item">
                              <span className="breakdown-icon">⏳</span>
                              <div className="breakdown-info">
                                <span className="breakdown-label">Pending Refunds</span>
                                <span className="breakdown-value yellow">{formatCurrency(pendingRefunds)}</span>
                                <span className="breakdown-hint">Being processed</span>
                              </div>
                            </div>
                          )}
                          {expectedRefunds > 0 && (
                            <div className="breakdown-item highlight">
                              <span className="breakdown-icon">💡</span>
                              <div className="breakdown-info">
                                <span className="breakdown-label">Expected Refunds</span>
                                <span className="breakdown-value green">{formatCurrency(expectedRefunds)}</span>
                                <span className="breakdown-hint">When campaigns complete</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Expected Refunds Detail */}
              {fundsData?.active_campaigns_with_excess?.length > 0 && (
                <div className="expected-refunds-detail">
                  <h3>💰 Expected Escrow Refunds</h3>
                  <p className="section-hint">These campaigns have cheaper creators who accepted, resulting in potential refunds when completed.</p>
                  <div className="expected-refunds-list">
                    {fundsData.active_campaigns_with_excess.map(campaign => (
                      <div key={campaign.campaign_id} className="expected-refund-card">
                        <div className="campaign-name">{campaign.campaign_name}</div>
                        <div className="refund-breakdown">
                          <div className="refund-row">
                            <span>Escrow Blocked:</span>
                            <span>{formatCurrency(campaign.escrow_amount)}</span>
                          </div>
                          <div className="refund-row">
                            <span>Creator Pool:</span>
                            <span>{formatCurrency(campaign.creator_pool)}</span>
                          </div>
                          <div className="refund-row">
                            <span>Active Creator Cost:</span>
                            <span>{formatCurrency(campaign.active_allocation)}</span>
                          </div>
                          <div className="refund-row highlight">
                            <span>Expected Refund:</span>
                            <span className="refund-amount">+{formatCurrency(campaign.expected_excess)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Campaign Funds Table */}
              <div className="funds-by-campaign">
                <div className="funds-by-campaign-header">
                  <h3>📊 Funds by Campaign</h3>
                  {fundsData?.campaigns?.length > 0 && (
                    <div className="funds-filters">
                      <div className="funds-search">
                        <span className="funds-search-icon">🔍</span>
                        <input
                          type="text"
                          className="funds-search-input"
                          placeholder="Search campaign name..."
                          value={fundsSearch}
                          onChange={e => setFundsSearch(e.target.value)}
                        />
                        {fundsSearch && (
                          <button
                            type="button"
                            className="funds-search-clear"
                            onClick={() => setFundsSearch('')}
                            aria-label="Clear search"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <select
                        className="funds-status-filter"
                        value={fundsStatusFilter}
                        onChange={e => setFundsStatusFilter(e.target.value)}
                      >
                        <option value="all">All statuses</option>
                        <option value="draft">Draft</option>
                        <option value="live">Live</option>
                        <option value="in_progress">In progress</option>
                        <option value="content_review">Content review</option>
                        <option value="monitoring">Monitoring</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  )}
                </div>

                {!fundsData?.campaigns || fundsData?.campaigns?.length === 0 ? (
                  <div className="empty-state">
                    <span>💰</span>
                    <p>No campaign funds to display</p>
                  </div>
                ) : (() => {
                  const allCampaigns = fundsData.campaigns || [];
                  const needle = fundsSearch.trim().toLowerCase();
                  const filtered = allCampaigns.filter(c => {
                    const nameMatch = !needle
                      || (c.name || '').toLowerCase().includes(needle);
                    const statusMatch = fundsStatusFilter === 'all'
                      || c.status === fundsStatusFilter;
                    return nameMatch && statusMatch;
                  });
                  const isFiltered = filtered.length !== allCampaigns.length;
                  const sumEscrow = filtered.reduce((s, c) => s + (c.escrow_amount || 0), 0);
                  const sumFee = filtered.reduce((s, c) => s + (c.platform_fee || 0), 0);
                  const sumPool = filtered.reduce((s, c) => s + (c.creator_pool || 0), 0);
                  const sumReleased = filtered.reduce((s, c) => s + (c.released || 0), 0);
                  return (
                    <>
                      {isFiltered && (
                        <div className="funds-filter-banner">
                          <span>
                            Showing <strong>{filtered.length}</strong> of{' '}
                            <strong>{allCampaigns.length}</strong> campaigns
                          </span>
                          <button
                            type="button"
                            className="funds-filter-reset"
                            onClick={() => {
                              setFundsSearch('');
                              setFundsStatusFilter('all');
                            }}
                          >
                            Clear filters
                          </button>
                        </div>
                      )}

                      {filtered.length === 0 ? (
                        <div className="empty-state">
                          <span>🔍</span>
                          <p>No campaigns match your filters</p>
                        </div>
                      ) : (
                        <div className="funds-table-wrapper">
                          <table className="funds-table enhanced">
                            <thead>
                              <tr>
                                <th>Campaign</th>
                                <th>Escrow</th>
                                <th>Platform Fee</th>
                                <th>Creator Pool</th>
                                <th>Released</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map(campaign => (
                                <tr
                                  key={campaign.id || campaign._id}
                                  className={campaign.escrow_amount > 0 ? 'has-escrow' : ''}
                                >
                                  <td className="campaign-name-cell">
                                    <span className="campaign-name">
                                      {campaign.name || 'Unnamed Campaign'}
                                    </span>
                                  </td>
                                  <td className="escrow-cell">
                                    {campaign.escrow_amount > 0 ? (
                                      <span className="escrow-value">{formatCurrency(campaign.escrow_amount)}</span>
                                    ) : (
                                      <span className="no-escrow">-</span>
                                    )}
                                  </td>
                                  <td className="fee-cell">
                                    {campaign.platform_fee > 0 ? (
                                      <span className="fee-value">{formatCurrency(campaign.platform_fee)}</span>
                                    ) : (
                                      <span className="no-fee">-</span>
                                    )}
                                  </td>
                                  <td className="pool-cell">
                                    {campaign.creator_pool > 0 ? (
                                      <span className="pool-value">{formatCurrency(campaign.creator_pool)}</span>
                                    ) : (
                                      <span className="no-pool">-</span>
                                    )}
                                  </td>
                                  <td className="released-cell">
                                    {campaign.released > 0 ? (
                                      <span className="released-value green">{formatCurrency(campaign.released)}</span>
                                    ) : (
                                      <span className="not-released">₹0</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`status-badge ${campaign.status}`}>
                                      {campaign.status?.replace(/_/g, ' ') || 'Unknown'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="totals-row">
                                <td>
                                  <strong>{isFiltered ? 'Filtered total' : 'Total'}</strong>
                                </td>
                                <td><strong>{formatCurrency(sumEscrow)}</strong></td>
                                <td><strong>{formatCurrency(sumFee)}</strong></td>
                                <td><strong>{formatCurrency(sumPool)}</strong></td>
                                <td><strong className="green">{formatCurrency(sumReleased)}</strong></td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Escrow Information */}
              <div className="expected-refunds-section">
                <h3>💡 How Escrow Works</h3>
                <div className="escrow-info-card">
                  <div className="escrow-info-content">
                    <span className="info-icon">🔒</span>
                    <div className="info-text">
                      <h4>Smart Escrow Protection</h4>
                      <p>
                        Your escrow is calculated based on the highest-priced creator combination to ensure campaign fulfillment. 
                        If lower-priced backup creators complete the campaign, you'll receive the difference as a refund automatically.
                      </p>
                    </div>
                  </div>
                  <div className="escrow-breakdown-mini">
                    <div className="escrow-mini-item">
                      <span className="mini-label">Escrow = Creator Cost + Platform Fee (3.5%)</span>
                    </div>
                    <div className="escrow-mini-item">
                      <span className="mini-label">Refunds are processed within 5-7 business days</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="quick-links-section">
                <h3>Quick Actions</h3>
                <div className="quick-links">
                  <button className="quick-link-btn" onClick={() => setActiveTab('cancelled')}>
                    🚫 View Cancelled Campaigns ({campaigns.cancelled.length})
                  </button>
                  <button className="quick-link-btn" onClick={() => setActiveTab('refunds')}>
                    ↩️ View Refunds ({refundsData?.summary?.total_refunds || 0})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Cancelled Campaigns Tab */}
          {activeTab === 'cancelled' && (
            <div className="tab-content">
              <h1>Cancelled Campaigns</h1>
              <p className="tab-description">Campaigns that have been cancelled and their refund status</p>

              {campaigns.cancelled.length === 0 ? (
                <div className="empty-state">
                  <span>✅</span>
                  <h3>No cancelled campaigns</h3>
                  <p>All your campaigns are active or completed</p>
                </div>
              ) : (
                <div className="cancelled-campaigns-list">
                  {campaigns.cancelled.map(campaign => {
                    const relatedRefund = refundsData?.refunds?.find(r => r.campaign_id === (campaign.id || campaign._id));
                    return (
                      <div key={campaign.id || campaign._id} className="cancelled-campaign-card">
                        <div className="campaign-header">
                          <div className="campaign-info">
                            <h3>{campaign.name || campaign.campaign_name}</h3>
                            <span className="cancelled-badge">🚫 Cancelled</span>
                          </div>
                          <div className="campaign-date">
                            Cancelled: {campaign.cancelled_at ? new Date(campaign.cancelled_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>

                        <div className="campaign-details-grid">
                          <div className="detail-item">
                            <span className="label">Total Funds Blocked</span>
                            <span className="value">
                              {formatCurrency(
                                relatedRefund?.escrow_amount
                                ?? campaign.escrow_amount
                                ?? campaign.funds_blocked
                                ?? 0
                              )}
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Creators Invited</span>
                            <span className="value">{campaign.selected_creators?.length || campaign.creator_count_expected || 0}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Niche</span>
                            <span className="value">{campaign.niche?.join(', ') || 'N/A'}</span>
                          </div>
                          <div className="detail-item">
                            <span className="label">Platform</span>
                            <span className="value">{campaign.platform || 'Instagram'}</span>
                          </div>
                        </div>

                        {campaign.cancellation_reason && (
                          <div className="cancellation-reason">
                            <span className="label">Cancellation Reason:</span>
                            <p>{campaign.cancellation_reason}</p>
                          </div>
                        )}

                        {!relatedRefund && refundsLoading && (
                          <div className="refund-status-box loading">
                            <h4>Refund Status</h4>
                            <div className="refund-details">
                              <div className="refund-row">
                                <span className="label">Loading refund details…</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {relatedRefund && (
                          <div className="refund-status-box">
                            <h4>Refund Status</h4>
                            <div className="refund-details">
                              <div className="refund-row">
                                <span className="label">Status:</span>
                                <span className={`status-badge ${relatedRefund.status}`}>
                                  {relatedRefund.display_status || relatedRefund.status}
                                </span>
                              </div>
                              <div className="refund-row">
                                <span className="label">Total Funds Blocked:</span>
                                <span className="value">{formatCurrency(relatedRefund.escrow_amount)}</span>
                              </div>
                              <div className="refund-row">
                                <span
                                  className="label"
                                  title="3.5% upfront platform fee — non-refundable once campaign is created"
                                >
                                  Platform Fee (3.5%):
                                </span>
                                <span className="value deduction">-{formatCurrency(relatedRefund.platform_fee)}</span>
                              </div>
                              {relatedRefund.kill_fee_deduction > 0 && (
                                <div className="refund-row">
                                  <span
                                    className="label"
                                    title="8.5% of fixed payment, deducted only for creators who did work before cancellation"
                                  >
                                    Kill Fee (8.5%):
                                  </span>
                                  <span className="value deduction">-{formatCurrency(relatedRefund.kill_fee_deduction)}</span>
                                </div>
                              )}
                              <div className="refund-row highlight">
                                <span className="label">Your Refund:</span>
                                <span className="value">{formatCurrency(relatedRefund.net_refund)}</span>
                              </div>
                            </div>
                            {relatedRefund.razorpay_refund_id && (
                              <div className="refund-reference">
                                <span className="label">Reference ID:</span>
                                <span className="value">{relatedRefund.razorpay_refund_id}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Refunds Tab */}
          {activeTab === 'refunds' && (
            <div className="tab-content">
              <h1>Refunds</h1>
              <p className="tab-description">Track the status of all your campaign refunds</p>

              {refundsLoading ? (
                <div className="loading-state">Loading refunds...</div>
              ) : !refundsData?.refunds || refundsData?.refunds?.length === 0 ? (
                <div className="empty-state">
                  <span>✅</span>
                  <h3>No refunds</h3>
                  <p>You don't have any refund requests yet</p>
                </div>
              ) : (
                <>
                  {/* Refund Summary Stats */}
                  <div className="refund-stats-row">
                    <div className="refund-stat-card">
                      <span className="stat-value">{refundsData?.summary?.completed_count || 0}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="refund-stat-card pending">
                      <span className="stat-value">{refundsData?.summary?.pending_count || 0}</span>
                      <span className="stat-label">Pending</span>
                    </div>
                    <div className="refund-stat-card">
                      <span className="stat-value">{formatCurrency(refundsData?.summary?.total_refunded || 0)}</span>
                      <span className="stat-label">Total Refunded</span>
                    </div>
                    <div className="refund-stat-card">
                      <span className="stat-value">{formatCurrency(refundsData?.summary?.total_pending || 0)}</span>
                      <span className="stat-label">Pending Amount</span>
                    </div>
                  </div>

                  {/* Refund Type Summary */}
                  <div className="refund-type-summary">
                    {(() => {
                      const types = {
                        campaign_cancellation: refundsData?.refunds?.filter(r => r.type === 'campaign_cancellation').length || 0,
                        escrow_excess: refundsData?.refunds?.filter(r => r.type === 'escrow_excess').length || 0,
                        unused_performance: refundsData?.refunds?.filter(r => r.type === 'unused_performance').length || 0
                      };
                      return (
                        <div className="type-badges">
                          {types.campaign_cancellation > 0 && (
                            <span className="type-badge cancellation">🚫 {types.campaign_cancellation} Cancellation{types.campaign_cancellation > 1 ? 's' : ''}</span>
                          )}
                          {types.escrow_excess > 0 && (
                            <span className="type-badge excess">🔄 {types.escrow_excess} Escrow Excess</span>
                          )}
                          {types.unused_performance > 0 && (
                            <span className="type-badge performance">📊 {types.unused_performance} Unused Performance</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Filter Tabs */}
                  <div className="refund-filter-tabs">
                    <button 
                      className={`filter-tab ${refundFilter === 'in_progress' ? 'active' : ''}`}
                      onClick={() => setRefundFilter('in_progress')}
                    >
                      🔄 In Progress ({refundsData?.refunds?.filter(r => ['pending', 'processing'].includes(r.status)).length || 0})
                    </button>
                    <button 
                      className={`filter-tab ${refundFilter === 'failed' ? 'active' : ''}`}
                      onClick={() => setRefundFilter('failed')}
                    >
                      ❌ Failed ({refundsData?.refunds?.filter(r => r.status === 'failed').length || 0})
                    </button>
                    <button 
                      className={`filter-tab ${refundFilter === 'completed' ? 'active' : ''}`}
                      onClick={() => setRefundFilter('completed')}
                    >
                      ✅ Completed ({refundsData?.refunds?.filter(r => r.status === 'completed').length || 0})
                    </button>
                  </div>

                  {/* Filtered Refunds List */}
                  <div className="refunds-list">
                    {refundsData.refunds
                      .filter(refund => {
                        if (refundFilter === 'in_progress') return ['pending', 'processing'].includes(refund.status);
                        if (refundFilter === 'failed') return refund.status === 'failed';
                        if (refundFilter === 'completed') return refund.status === 'completed';
                        return true;
                      })
                      .length === 0 ? (
                        <div className="empty-refunds" style={{ marginTop: '16px' }}>
                          <span className="empty-icon">📭</span>
                          <p>No {refundFilter === 'in_progress' ? 'in-progress' : refundFilter} refunds</p>
                        </div>
                      ) : (
                        refundsData.refunds
                          .filter(refund => {
                            if (refundFilter === 'in_progress') return ['pending', 'processing'].includes(refund.status);
                            if (refundFilter === 'failed') return refund.status === 'failed';
                            if (refundFilter === 'completed') return refund.status === 'completed';
                            return true;
                          })
                          .map(refund => (
                            <div key={refund.refund_id || refund._id} className={`refund-card status-${refund.status}`}>
                              <div className="refund-header">
                                <div className="refund-campaign">
                                  <div className="refund-title-row">
                                    <h4>{refund.campaign_name}</h4>
                                    <span className={`refund-type-tag ${refund.type}`}>
                                      {refund.type === 'campaign_cancellation' ? '🚫 Cancellation' : 
                                       refund.type === 'escrow_excess' ? '🔄 Escrow Excess' :
                                       refund.type === 'unused_performance' ? '📊 Unused Performance' : '💰 Refund'}
                                    </span>
                                  </div>
                                  <span className="refund-date">
                                    {refund.created_at ? new Date(refund.created_at).toLocaleDateString() : ''}
                                  </span>
                                </div>
                                <div className={`refund-status-badge ${refund.status_color}`}>
                                  {refund.display_status}
                                </div>
                              </div>

                              <div className="refund-breakdown">
                                <div className="breakdown-item">
                                  <span className="label">Original Escrow</span>
                                  <span className="value">{formatCurrency(refund.escrow_amount)}</span>
                                </div>
                                <div className="breakdown-item deduction">
                                  <span className="label">Platform Fee (3.5%)</span>
                                  <span className="value">-{formatCurrency(refund.platform_fee)}</span>
                                </div>
                                <div className="breakdown-item">
                                  <span className="label">Fixed paid to creators</span>
                                  <span className="breakdown-sublabel">
                                    Gross from your creator pool (incl. Milan payout fees
                                    {refund.creator_fees_on_fixed > 0
                                      ? `: ${formatCurrency(refund.creator_fees_on_fixed)}`
                                      : ''}
                                    )
                                  </span>
                                  <span className="value">{formatCurrency(refund.released_fixed_gross ?? 0)}</span>
                                </div>
                                <div className="breakdown-item">
                                  <span className="label">Performance paid to creators</span>
                                  <span className="breakdown-sublabel">
                                    Gross from your creator pool (incl. Milan payout fees
                                    {refund.creator_fees_on_performance > 0
                                      ? `: ${formatCurrency(refund.creator_fees_on_performance)}`
                                      : ''}
                                    )
                                  </span>
                                  <span className="value">{formatCurrency(refund.released_performance_gross ?? 0)}</span>
                                </div>
                                <div
                                  className={`breakdown-item${(refund.unused_performance_pool ?? 0) > 0 ? ' breakdown-unused-perf' : ''}`}
                                >
                                  <span className="label">Unused performance pool</span>
                                  <span className="breakdown-sublabel">
                                    Performance budget not paid out to creators (often part of escrow excess refunds)
                                  </span>
                                  <span className="value">{formatCurrency(refund.unused_performance_pool ?? 0)}</span>
                                </div>
                                {refund.type === 'campaign_cancellation' && Number(refund.kill_fee_deduction) > 0 && (
                                  <div className="breakdown-item deduction">
                                    <span className="label">Cancellation payouts (from escrow)</span>
                                    <span className="value">-{formatCurrency(refund.kill_fee_deduction)}</span>
                                  </div>
                                )}
                                <div className="breakdown-item total">
                                  <span className="label">Your Refund</span>
                                  <span className="value">{formatCurrency(refund.net_refund || refund.amount)}</span>
                                </div>
                              </div>

                              <div className={`refund-message ${refund.status_color}`}>
                                <span className="info-icon">
                                  {refund.status === 'completed' ? '✅' : 
                                   refund.status === 'failed' ? '🔄' : 
                                   refund.status === 'processing' ? '⏳' : 'ℹ️'}
                                </span>
                                {refund.status_message}
                              </div>

                              {refund.razorpay_refund_id && (
                                <div className="refund-reference">
                                  <span className="label">Reference ID:</span>
                                  <span className="value">{refund.razorpay_refund_id}</span>
                                </div>
                              )}
                            </div>
                          ))
                      )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="tab-content settings-page">
              <h1>Settings</h1>
              <p className="tab-description">Manage your account settings and preferences</p>

              {/* Bank Details Section */}
              <div className="settings-section">
                <div className="section-header">
                  <h2>🏦 Bank Details</h2>
                  <p className="section-description">
                    Add your bank details for receiving refunds. This information is optional and only used when processing refunds.
                  </p>
                </div>

                {bankDetailsLoading ? (
                  <div className="loading-state">Loading bank details...</div>
                ) : (
                  <div className="bank-details-card">
                    {bankDetailsMessage.text && (
                      <div className={`message-banner ${bankDetailsMessage.type}`}>
                        {bankDetailsMessage.type === 'success' ? '✅' : '❌'} {bankDetailsMessage.text}
                      </div>
                    )}

                    <form onSubmit={handleSaveBankDetails} className="bank-details-form">
                      {/* Payment Method Toggle */}
                      <div className="form-group">
                        <label>Payment Method</label>
                        <div className="payment-method-toggle">
                          <button
                            type="button"
                            className={`method-btn ${bankDetailsForm.payment_method === 'bank' ? 'active' : ''}`}
                            onClick={() => setBankDetailsForm(prev => ({ ...prev, payment_method: 'bank' }))}
                          >
                            🏦 Bank Transfer
                          </button>
                          <button
                            type="button"
                            className={`method-btn ${bankDetailsForm.payment_method === 'upi' ? 'active' : ''}`}
                            onClick={() => setBankDetailsForm(prev => ({ ...prev, payment_method: 'upi' }))}
                          >
                            📱 UPI
                          </button>
                        </div>
                      </div>

                      {bankDetailsForm.payment_method === 'upi' ? (
                        <div className="form-group">
                          <label>UPI ID *</label>
                          <input
                            type="text"
                            placeholder="yourname@upi"
                            value={bankDetailsForm.upi_id}
                            onChange={(e) => setBankDetailsForm(prev => ({ ...prev, upi_id: e.target.value }))}
                            required
                          />
                          <span className="field-hint">Example: yourname@paytm, yourname@ybl</span>
                        </div>
                      ) : (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Account Holder Name *</label>
                              <input
                                type="text"
                                placeholder="As per bank records"
                                value={bankDetailsForm.account_holder_name}
                                onChange={(e) => setBankDetailsForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Bank Name</label>
                              <input
                                type="text"
                                placeholder="e.g., HDFC Bank"
                                value={bankDetailsForm.bank_name}
                                onChange={(e) => setBankDetailsForm(prev => ({ ...prev, bank_name: e.target.value }))}
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Account Number *</label>
                              <input
                                type="text"
                                placeholder="Enter account number"
                                value={bankDetailsForm.account_number}
                                onChange={(e) => setBankDetailsForm(prev => ({ ...prev, account_number: e.target.value }))}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>IFSC Code *</label>
                              <input
                                type="text"
                                placeholder="e.g., HDFC0001234"
                                value={bankDetailsForm.ifsc_code}
                                onChange={(e) => setBankDetailsForm(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                                maxLength={11}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Account Type</label>
                            <select
                              value={bankDetailsForm.account_type}
                              onChange={(e) => setBankDetailsForm(prev => ({ ...prev, account_type: e.target.value }))}
                            >
                              <option value="savings">Savings Account</option>
                              <option value="current">Current Account</option>
                            </select>
                          </div>
                        </>
                      )}

                      <div className="form-actions">
                        <button 
                          type="submit" 
                          className="save-btn"
                          disabled={savingBankDetails}
                        >
                          {savingBankDetails ? 'Saving...' : (bankDetails ? 'Update Bank Details' : 'Save Bank Details')}
                        </button>
                        {bankDetails && (
                          <button 
                            type="button" 
                            className="delete-btn"
                            onClick={handleDeleteBankDetails}
                          >
                            Delete Bank Details
                          </button>
                        )}
                      </div>
                    </form>

                    {bankDetails && (
                      <div className="current-details">
                        <h4>Current Bank Details</h4>
                        <div className="details-grid">
                          <div className="detail-item">
                            <span className="label">Payment Method</span>
                            <span className="value">{bankDetails.payment_method === 'upi' ? '📱 UPI' : '🏦 Bank Transfer'}</span>
                          </div>
                          {bankDetails.payment_method === 'upi' ? (
                            <div className="detail-item">
                              <span className="label">UPI ID</span>
                              <span className="value">{bankDetails.upi_id}</span>
                            </div>
                          ) : (
                            <>
                              <div className="detail-item">
                                <span className="label">Account Holder</span>
                                <span className="value">{bankDetails.account_holder_name}</span>
                              </div>
                              <div className="detail-item">
                                <span className="label">Account Number</span>
                                <span className="value">****{bankDetails.account_number?.slice(-4)}</span>
                              </div>
                              <div className="detail-item">
                                <span className="label">IFSC Code</span>
                                <span className="value">{bankDetails.ifsc_code}</span>
                              </div>
                              {bankDetails.bank_name && (
                                <div className="detail-item">
                                  <span className="label">Bank</span>
                                  <span className="value">{bankDetails.bank_name}</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      <Footer />

      {/* Cancel Campaign Modal */}
      <CancelCampaignModal
        isOpen={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCampaignToCancel(null);
        }}
        campaignId={campaignToCancel?.id || campaignToCancel?._id}
        campaignName={campaignToCancel?.name || campaignToCancel?.campaign_name}
        onCancelled={(result) => {
          // Move from live campaigns to cancelled
          const cancelledCampaign = campaignToCancel ? { ...campaignToCancel, status: 'cancelled' } : null;
          setCampaigns(prev => ({
            ...prev,
            live: prev.live.filter(c =>
              (c.id || c._id) !== (campaignToCancel?.id || campaignToCancel?._id)
            ),
            cancelled: cancelledCampaign ? [...prev.cancelled, cancelledCampaign] : prev.cancelled
          }));
          // Refresh refunds data
          fetchRefunds();
          // Clear selected campaign
          setSelectedCampaign(null);
          setCampaignDetails(null);
          setCampaignDetailsError(null);
          setCampaignPerformance(null);
          // Navigate back to campaigns list
          navigate('/brand-dashboard?tab=live', { replace: true });
          // Refresh dashboard data
          fetchDashboardData();
        }}
      />
    </div>
  );
}

export default BrandDashboard;
